// Lightweight bilingual (English / 中文) i18n. Both languages live in one
// dictionary; a header toggle flips between them and the choice persists in
// localStorage. Chinese is the first-load default (the client's daily language).
//
// `t(key, vars)` does `{name}` interpolation and a regular-plural `{s}` token
// (dropped when a numeric `n` var === 1). Keys are typed off the `en` dict, so a
// typo'd key is a compile error and `zh` must define every key.

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type Lang = "en" | "zh";

const STORAGE_KEY = "dispatch.lang";

const en = {
  // ── App shell ──
  "app.brand": "Dispatch Planner",
  "app.signOut": "Sign out",
  "app.loading": "Loading…",
  "app.submitting": "Submitting…",
  "app.optimizing": "Optimizing…",
  "app.tabOrders": "Pick from warehouse",
  "app.tabUpload": "Upload CSV",
  "app.tabHistory": "Past runs",
  "app.tabDeliveries": "Deliveries",
  "app.tabCosts": "3PL costs",
  "costs.title": "3PL costs",
  "costs.desc": "Each booked 3PL truckload's negotiated and invoiced charges. Type in a carrier's invoice, check it against what was agreed, then reconcile. Each truckload's cost is split over its orders by the miles each stop adds; reconciling freezes the split.",
  "costs.from": "From",
  "costs.to": "To",
  "costs.carrier": "Carrier",
  "costs.allCarriers": "All carriers",
  "costs.statusFilter": "Cost status",
  "costs.allStatuses": "All",
  "costs.status.open": "Not invoiced",
  "costs.status.invoiced": "Invoiced",
  "costs.status.reconciled": "Reconciled",
  "costs.export": "Export CSV",
  "costs.exportOrders": "Export by order",
  "costs.col.allocation": "Split",
  "costs.alloc.state.frozen": "Frozen",
  "costs.alloc.state.provisional": "Provisional",
  "costs.alloc.state.cancelled": "Not split (cancelled)",
  "costs.alloc.state.no_orders": "Not split (no orders)",
  "costs.alloc.state.no_cost": "No cost yet",
  "costs.alloc.changed": "Truck changed since split",
  "costs.alloc.estimateOnly": "No cost entered: estimate only",
  "costs.alloc.unallocated": "{usd} in charges on this cancelled booking isn't split over any order.",
  "costs.alloc.title": "Cost by order",
  "costs.alloc.basis.reconciled": "reconciled cost",
  "costs.alloc.basis.invoiced": "invoiced total",
  "costs.alloc.basis.negotiated": "negotiated total",
  "costs.alloc.basis.estimate": "model's estimate",
  "costs.alloc.splitting": "Splitting the {basis}, {usd}, by the miles each stop adds.",
  "costs.alloc.frozenBy": "Frozen at reconcile by {who}, {when}.",
  "costs.alloc.changedHint": "The truck's trips or orders changed after it was reconciled. Reopen and reconcile again to split it as it is now.",
  "costs.alloc.provisionalHint": "Provisional: it follows the plan and the charges until reconciled.",
  "costs.alloc.trip": "Trip {n} · {route}",
  "costs.alloc.open": "ends at its last drop",
  "costs.alloc.loop": "back to the depot",
  "costs.alloc.sampled": "sampled (many stops)",
  "costs.alloc.stop": "Stop",
  "costs.alloc.zip": "ZIP",
  "costs.alloc.miles": "Miles share",
  "costs.alloc.share": "Share",
  "costs.alloc.order": "Order",
  "costs.alloc.pallets": "Pallets",
  "costs.alloc.amount": "Amount",
  "costs.alloc.total": "Allocated {allocated} of {total}",
  "costs.alloc.freezeHint": "Reconciling freezes the cost and its split by order.",
  "costs.loadFailed": "Couldn't load the costs: {msg}",
  "costs.empty": "No booked 3PL truckloads in these dates.",
  "costs.col.date": "Date",
  "costs.col.truck": "Truck",
  "costs.col.carrier": "Carrier",
  "costs.col.orders": "Orders",
  "costs.col.negotiated": "Negotiated",
  "costs.col.invoiced": "Invoiced",
  "costs.col.variance": "Variance",
  "costs.col.status": "Status",
  "costs.col.warnings": "Warnings",
  "costs.cancelled": "Booking cancelled",
  "costs.details": "Details",
  "costs.lines": "Charge lines",
  "costs.basis.negotiated": "Negotiated",
  "costs.basis.invoiced": "Invoiced",
  "costs.voided": "voided: {reason}",
  "costs.invoices": "Invoices",
  "costs.invoiceLine": "{no} · {date} · {total}",
  "costs.invoiceVoided": "voided by {who}: {reason}",
  "costs.voidInvoice": "Void invoice",
  "costs.voidReason": "Why void this invoice? Its lines are voided with it.",
  "costs.deliveries": "{delivered} of {orders} orders delivered",
  "costs.warn.pod_missing": "Delivered without a POD: {orders}",
  "costs.warn.not_delivered": "Not delivered: {orders}",
  "costs.warn.no_outcome": "No outcome recorded: {orders}",
  "costs.reconcile": "Reconcile at {usd}",
  "costs.reconciled": "Reconciled at {usd} by {who}",
  "costs.reopen": "Reopen",
  "costs.reopenReason": "Why reopen this cost?",
  "costs.documents": "Documents",
  "costs.noDocuments": "No documents yet.",
  "costs.doc.rate_confirmation": "Rate confirmation",
  "costs.doc.invoice": "Invoice",
  "costs.doc.other": "Other",
  "costs.doc.kind": "Document kind",
  "costs.doc.file": "Document file",
  "costs.doc.upload": "Upload",
  "costs.doc.remove": "Remove",
  "costs.doc.removeConfirm": "Remove this document? Who removed it is kept.",
  "costs.history": "History",
  "costs.openPlan": "Open plan",
  "costs.inv.open": "Record an invoice",
  "costs.inv.title": "Carrier invoice",
  "costs.inv.number": "Invoice number",
  "costs.inv.date": "Invoice date",
  "costs.inv.serviceDate": "Day of work",
  "costs.inv.total": "Invoice total",
  "costs.inv.note": "Note",
  "costs.inv.truck": "Truckload",
  "costs.inv.pickTruck": "Choose a truckload",
  "costs.inv.kind": "Kind",
  "costs.inv.code": "Code (e.g. detention)",
  "costs.inv.amount": "Amount",
  "costs.inv.addLine": "Add a line",
  "costs.inv.removeLine": "Remove line",
  "costs.inv.sum": "Lines add up to {sum} of {total}",
  "costs.inv.save": "Save invoice",
  "costs.inv.cancel": "Cancel",
  "app.tabPlans": "Dispatch plans",

  // ── Saved dispatch plans ──
  "plans.title": "Dispatch plans",
  "plans.desc": "Your saved dispatch plans, newest plan date first. Open one to keep editing, publish it or export it.",
  "plans.dateFrom": "Plan date from",
  "plans.dateTo": "Plan date to",
  "plans.table": "Saved dispatch plans",
  "plans.col.date": "Plan date",
  "plans.col.warehouse": "Warehouse",
  "plans.col.status": "Status",
  "plans.col.size": "Trucks and orders",
  "plans.col.saved": "Last saved",
  "plans.col.actions": "",
  "plans.trucks": "{n} truck{s}",
  "plans.orders": "{n} order{s}",
  "plans.unrouted": "{n} not on a route",
  "plans.threeplUnassigned": "{n} 3PL truck{s} without a carrier",
  "plans.open": "Open",
  "plans.openFor": "Open plan for {date}",
  "plans.empty": "No saved plans yet. Open a run's dispatch plan to start one.",
  "plans.loadFailed": "Couldn't load the plans:",

  // ── Login ──
  "login.subtitle": "Sign in to continue.",
  "login.username": "Username",
  "login.password": "Password",
  "login.signIn": "Sign in",
  "login.signingIn": "Signing in…",

  // ── Picker ──
  "picker.heading": "Select orders",
  "picker.desc":
    "List of arrived warehouse orders. Tick the orders to deliver or add one " +
    "manually; selected orders gather in the order builder on the right, where " +
    "you can set delivery time windows and confirm each pallet's dimensions.",
  "picker.arrivedWithin": "Arrived within",
  "picker.allTime": "All time",
  "picker.regionLabel": "Warehouse",
  "picker.regionAll": "All warehouses",
  "region.socal": "Southern California",
  "region.east": "New Jersey / New York / Pennsylvania",
  "region.none": "Outside our service area",
  "picker.daysN": "{d} days",
  "picker.searchLabel": "Search order # / customer",
  "picker.searchPlaceholder": "e.g. AN2603…",
  "picker.search": "Search",
  "picker.viewAll": "All arrivals",
  "picker.viewSelected": "Selected ({n})",
  "picker.emptyArrivals": "No arrived orders in this window.",
  "picker.emptySelected": "No orders selected yet.",
  "picker.loadingOrders": "Loading orders…",
  "picker.coldStartHint": "First load can take 10–15 seconds while the server warms up.",
  "picker.legendMeasured": "= real carton dimensions",
  "picker.legendEstimated":
    "= derived from total volume (no per-pallet measurements — confirm or edit on the right)",
  "picker.orderBuilder": "Order builder",
  "picker.modeOptimize": "Optimize",
  "picker.modeEvaluate": "Evaluate my plan",
  "picker.clearAll": "Clear all",
  "picker.clearConfirm": "Clear all orders from the builder?",
  "picker.evalInfo":
    "Assign each order a Trip and Truck. Orders sharing a trip ride together; " +
    "trips sharing a truck run back-to-back. We'll show where each trip breaks " +
    "the limits — instead of optimizing.",
  "picker.saveDraft": "Save draft",
  "picker.draftsN": "Drafts ({n}) ▾",
  "picker.load": "Load",
  "picker.noDrafts": "No saved drafts.",
  "picker.saveDraftTip": "Save this composition as a draft",
  "picker.draftsUnavailableTip": "Drafts unavailable — backend persistence not configured",
  "picker.addManual": "+ Add order manually",
  "picker.pasteOpen": "− Paste order numbers",
  "picker.pasteClosed": "+ Paste order numbers",
  "picker.emptyCart": "Tick orders on the left, or add one manually.",
  "picker.pastePlaceholder": "Paste order numbers — newline, comma, or space separated",
  "picker.addOrders": "Add orders",
  "picker.adding": "Adding…",
  "picker.pasteNothing": "Nothing to add — paste order numbers first.",
  "picker.pasteResult": "Added {added} · already in cart {already} · not found {missing}",
  "picker.pasteNotFound": "Not found (check for typos): {list}",
  "picker.manualPlaceholder": "Order # / label",
  "picker.manualBadge": "manual",
  "picker.unnamed": "(unnamed)",
  "picker.trip": "Trip",
  "picker.truck": "Truck",
  "picker.deliverBy": "Deliver by",
  "picker.notBefore": "Not before",
  "picker.timeAny": "Any",
  "picker.palletUnits": "L × W × H in cm · weight in kg",
  "picker.edited": "edited",
  "picker.validationZip": "Order “{name}” needs a destination ZIP.",
  "picker.buildPlan": "Build plan for {n} order{s}",
  "picker.evaluatePlan": "Evaluate my plan ({n} trip{s})",
  "picker.evaluateTip":
    "Run your trip/truck assignment as-is and see where it breaks the limits",
  "picker.cartOrders": "{n} order{s}",
  "picker.cartPallets": "{n} pallet{s}",
  "picker.truckTripShort": "Truck {truck} · Trip {trip}",
  "picker.loadingPallets": "Loading pallets…",
  "picker.estimatedDimsWarning":
    "No dimension data in the system for this order — pallets below are a default-size estimate (120×100). Please verify and enter the real dimensions.",
  "picker.addPallet": "+ Add pallet",
  "picker.removeOrderTip": "Remove",
  "picker.removePalletTip": "Remove pallet",
  "picker.draftDeleteTip": "Delete draft",
  "picker.draftNamePrompt": "Name this draft",
  "picker.draftDeleteConfirm": "Delete this draft?",

  // ── Fleet labels (shared) ──
  "fleet.26ft": "26 ft",
  "fleet.53ft": "53 ft",
  // Up to this many: the optimizer uses only the trucks that pay their way.
  "fleet.available26": "26 ft available",
  "fleet.available53": "53 ft available",
  "fleet.available3pl": "3PL trucks",
  "fleet.truckOf": "{truck} · {size}",
  "fleet.departsOf": "{truck} departs",

  // ── Upload ──
  "upload.heading": "Plan tomorrow's dispatch",
  "upload.desc":
    "Upload the day's shipment CSV. The planner assigns pallets to your fleet, " +
    "sequences every route, and lays out each trailer.",
  "upload.drop": "Drop the shipment CSV here, or click to browse",
  "upload.cols":
    "Needs: shipment ID, pallet dimensions, weight, destination ZIP · " +
    "Optional: deadlines, arrival windows, delivery groups, trip_id, truck_id",
  "upload.fleet26": "26 ft trucks available",
  "upload.fleet53": "53 ft trucks available",
  "upload.fleet3pl": "3PL trucks",
  "upload.build": "Build dispatch plan",
  "upload.evaluate": "Evaluate plan as-is",
  "upload.evaluateTip":
    "Run the CSV's trip_id grouping as-is and show where each trip breaks the limits",
  "upload.evaluateExplainer":
    "Evaluate plan as-is runs your own assignment columns — trip_id (orders " +
    "sharing a value ride one trip) and optional truck_id (trips sharing a truck " +
    "run back-to-back) — and flags where each trip breaks weight, capacity, " +
    "hours, or time windows.",

  "upload.checking": "Checking the file…",
  "upload.blockers":
    "{n} pallet{s} can't ship with this fleet. Add a bigger trailer, fix " +
    "the file, or build the plan anyway — whatever can't be loaded is " +
    "listed in the result.",
  "upload.warnings": "Possible data entry error{s} — {n} row{s} to check:",

  // ── Progress ──
  "progress.building": "Building your dispatch plan",
  "progress.elapsed": "elapsed",
  "progress.ofBudget": "of up to {budget}",
  "progress.stageDistances": "Measuring the routes",
  "progress.stageAssign": "Assigning pallets to trucks",
  "progress.stageRoute": "Sequencing each trip",
  "progress.stageFinalize": "Costing the plan",
  "progress.retry": "refining, attempt {n}",
  "progress.hint":
    "Larger days take longer — the planner keeps refining routes until its time " +
    "budget runs out.",

  // ── Results ──
  "results.titlePlan": "Dispatch plan",
  "results.titleEval": "Client plan check",
  "results.evalSubtitle": "Your proposed trips, run as-is · ",
  "results.meta": "{p} pallets · {mi} mi · {h} h",
  "results.print": "Print driver sheets",
  "results.newPlan": "← New plan",
  "results.routeMap": "Route map",
  "results.pnl": "Profit & loss",
  "results.threepl": "Third-party routes",
  "results.truckDetails": "Truck details",

  // ── Summary ──
  "summary.netProfit": "Net profit",
  "summary.pallets": "Pallets",
  "summary.threepl": "3PL",
  "summary.miles": "Miles",
  "summary.hours": "Hours",
  "summary.revMinusCost": "Revenue {rev} − Cost {cost}",
  "summary.onOwnFleet": "{n} on own fleet",
  "summary.routedSolo": "{routed} routed · {solo} solo",
  "summary.no3pl": "No 3PL needed",
  "summary.totalDistance": "Total route distance",
  "summary.driverHourTotal": "Driver-hour total",
  "summary.fleetSummary": "Fleet summary",
  "summary.totalPallets": "Total pallets",
  "summary.ownTruck": "Own truck",
  "summary.threeplRoutes": "3PL routes",
  "summary.threeplRoutesVal": "{p} pallets / {r} route(s)",
  "summary.threeplSolo": "3PL solo (oversized)",
  "summary.unassigned": "Unassigned",
  "summary.ownFleetTrips": "Own-fleet trips",
  "summary.totalMiles": "Total miles",
  "summary.totalHours": "Total hours",
  "summary.perTruck": "Per truck",
  "summary.pnlTitle": "Profit & loss",
  "summary.revenue": "Revenue",
  "summary.fixedCost": "Fixed cost (sunk)",
  "summary.driverCost": "Driver cost",
  "summary.fuelCost": "Fuel cost",
  "summary.threeplRoutesCost": "3PL routes cost",
  "summary.threeplSoloCost": "3PL solo cost",
  "summary.totalCost": "Total cost",
  "summary.alertBadPlan":
    "The planner could not produce a usable plan (status: {status}). Check the " +
    "shipment data and try again.",
  "summary.alertInfeasible": "{n} pallet(s) could not be placed on any vehicle:",
  "summary.alertSolo3pl":
    "{n} pallet(s) (~${cost}) went to expensive per-pallet (solo) 3PL — add a truck to keep them on our fleet.",

  // ── Truck card ──
  "truck.header": "Truck {label} · Trip {n}",
  "truck.weight": "Weight",
  "truck.area": "Area",
  "truck.lunch": "🍽 Lunch break {start} – {end}",
  "truck.departs": "Loads {load} · departs {depart}",
  "truck.etaPrefix": "ETA: ",
  "truck.twAfter": "After {t}",
  "truck.twEarly": "(early)",
  "truck.twBy": "By {t}",
  "truck.twLate": "({min} min late)",
  "truck.devWeight": "Over weight by {kg} kg ({pct}% of cap)",
  "truck.devOverweight": "Overweight — exceeds truck cap",
  "truck.devCapacity": "Over floor capacity — {pct}% (won't fit one trailer)",
  "truck.devHours": "Over daily hours — {h}h, {over}h past the 12h limit",
  "truck.devLate": "{n} late deliveries — up to {min} min past deadline",
  "truck.devEarly": "{n} stops arrive before the allowed window",
  "truck.devTruckDay": "Truck {label} runs {h}h across its {trips} trips — over the 12h day",
  "truck.threeplMeta": "{stops} stops · {mi} mi · ${cost}",

  // ── Map / trailer popups ──
  "map.depot": "Depot",
  "map.truck": "Truck",
  "map.stop": "Stop",
  "map.eta": "ETA",
  "map.stopsUnit": "stops",
  "map.unrouted": "Waiting for a trip",
  "trailer.rotated": "(rotated)",

  // ── 3PL bookings ──
  "bookings.title": "3PL bookings",
  "bookings.summary": "{booked} of {n} 3PL trucks booked",
  "bookings.unassigned": "{n} without a carrier",
  "bookings.requested": "{n} requested",
  "bookings.totals": "negotiated {negotiated} against the model's {estimate}",
  "bookings.saveFirst": "Recalibrate to save your changes before booking.",
  "bookings.col.truck": "Truck",
  "bookings.col.load": "Load",
  "bookings.col.carrier": "Carrier",
  "bookings.col.state": "Booking",
  "bookings.col.estimate": "Estimate",
  "bookings.col.negotiated": "Negotiated",
  "bookings.truck": "3PL truck {truck}",
  "bookings.trips": "{n} trip{s}",
  "bookings.pallets": "{n} pallet{s}",
  "bookings.bookedElsewhere": "Also on another plan's booked truck: {orders}",
  "bookings.choose": "Choose a carrier",
  "bookings.newCarrier": "New carrier",
  "bookings.carrierName": "Carrier name",
  "bookings.add": "Add",
  "bookings.similar": "Already in the list: {name}",
  "bookings.similarInactive": "Already in the list, deactivated: {name}",
  "bookings.useIt": "Use {name}",
  "bookings.reactivate": "Reactivate {name}",
  "bookings.editCarrier": "Edit carrier",
  "bookings.carrierDetails": "{name} details",
  "bookings.field.name": "Name",
  "bookings.field.contact_name": "Contact",
  "bookings.field.phone": "Phone",
  "bookings.field.email": "Email",
  "bookings.field.notes": "Notes",
  "bookings.field.ushipment_supplier_id": "uShipment supplier id",
  "bookings.save": "Save",
  "bookings.deactivate": "Deactivate",
  "bookings.deactivateConfirm":
    "Deactivate {name}? It can't be booked again until it's reactivated; its bookings stay.",
  "bookings.state.unassigned": "No carrier",
  "bookings.state.requested": "Requested",
  "bookings.state.booked": "Booked",
  "bookings.changed": "Changed since it was booked",
  "bookings.book": "Book",
  "bookings.request": "Request",
  "bookings.markBooked": "Mark booked",
  "bookings.informed": "Carrier informed",
  "bookings.cancelBooking": "Cancel booking",
  "bookings.cancelRequest": "Cancel request",
  "bookings.cancelReason": "Why is it cancelled?",
  "bookings.details": "Charges and history",
  "bookings.chargesFor": "Charges for {truck}",
  "bookings.kind": "Kind",
  "bookings.kind.linehaul": "Linehaul",
  "bookings.kind.accessorial": "Accessorial",
  "bookings.kind.adjustment": "Adjustment",
  "bookings.amount": "Amount ($)",
  "bookings.code": "Code (e.g. liftgate)",
  "bookings.note": "Note",
  "bookings.addCharge": "Add charge",
  "bookings.void": "Void",
  "bookings.voidReason": "Why is this charge voided?",
  "bookings.voided": "voided: {reason}",
  "bookings.wasCancelled": "{carrier} · cancelled: {reason}",

  // ── Deliveries (read from Samsara) ──
  "deliveries.title": "Deliveries",
  "deliveries.desc":
    "Every order on the day's sent routes, as Samsara reports it: when it left the " +
    "warehouse, reached the drop and was delivered, with its proof of delivery stamped " +
    "with ANL and where it was taken. Refresh from Samsara reads the day now; each night, " +
    "finished routes are closed out. Send an order's share link to its customer.",
  "deliveries.depot": "Warehouse",
  "deliveries.date": "Plan date",
  "deliveries.attentionOnly": "Needs attention only",
  "deliveries.summary": "Day summary",
  "deliveries.sum.orders": "Orders",
  "deliveries.sum.in_progress": "In progress",
  "deliveries.sum.delivered": "Delivered",
  "deliveries.sum.exceptions": "Exceptions",
  "deliveries.sum.not_delivered": "Not delivered",
  "deliveries.sum.on_time": "On time",
  "deliveries.sum.pod_missing": "POD missing",
  "deliveries.sum.needs_attention": "Needs attention",
  "deliveries.refresh": "Refresh from Samsara",
  "deliveries.refreshing": "Reading Samsara…",
  "deliveries.refreshFailed": "Couldn't refresh: {msg}",
  "deliveries.simulated": "Samsara isn't connected here (simulated publishing), so nothing was read.",
  "deliveries.routesFailed": "Couldn't read {routes} from Samsara ({reason}); the rest are up to date.",
  "deliveries.lastRefresh": "Last read from Samsara {time}",
  "deliveries.neverRefreshed": "Not read from Samsara yet",
  "deliveries.notRead": "This day has sent routes but hasn't been read from Samsara yet.",
  "deliveries.lastRun": "Last closed out {time}",
  "deliveries.timesIn": "Times in {zone}",
  "deliveries.clock.title": "Clock in / out",
  "deliveries.clock.hint": "From each driver's start- and end-of-shift inspection in Samsara.",
  "deliveries.clock.driver": "Driver",
  "deliveries.clock.in": "Clock in",
  "deliveries.clock.out": "Clock out",
  "deliveries.clock.hours": "Hours",
  "deliveries.clock.duration": "{h} h {m} min",
  "deliveries.neverRun": "Not closed out yet",
  "deliveries.loadFailed": "Couldn't load deliveries: {msg}",
  "deliveries.empty": "No delivery records for this day.",
  "deliveries.col.order": "Order",
  "deliveries.col.customer": "Customer",
  "deliveries.col.drop": "Drop",
  "deliveries.col.truck": "Truck · carrier",
  "deliveries.col.window": "Window",
  "deliveries.col.pallets": "Pallets",
  "deliveries.col.stage": "Status",
  "deliveries.col.attention": "Needs attention",
  "deliveries.reviewed": "Reviewed",
  "deliveries.review.title": "Needs attention",
  "deliveries.review.note": "What was done about it",
  "deliveries.review.submit": "Mark reviewed",
  "deliveries.review.done": "Reviewed by {who} at {time}: {note}",
  "deliveries.col.pod": "POD",
  "deliveries.stage.scheduled": "Scheduled",
  "deliveries.stage.left_depot": "Left depot",
  "deliveries.stage.at_drop": "At the drop",
  "deliveries.stage.delivered": "Delivered",
  "deliveries.stage.delivery_exception": "Exception",
  "deliveries.stage.not_delivered": "Not delivered",
  "deliveries.open": "open",
  "deliveries.openHint": "The route is still running, so this may still change.",
  "deliveries.eta": "ETA {time}",
  "deliveries.by": "by {time}",
  "deliveries.after": "after {time}",
  "deliveries.ago.min": "{n} min ago",
  "deliveries.ago.hour": "{n} h ago",
  "deliveries.pod.driver": "Driver",
  "deliveries.pod.dispatcher": "Dispatcher",
  "deliveries.proof.title": "Proof from the dispatcher",
  "deliveries.proof.hint":
    "No proof from the driver? Upload the signed BOL or a photo of the pallets " +
    "(a photo or a PDF, up to 10 MB).",
  "deliveries.proof.file": "File",
  "deliveries.proof.kind": "What it shows",
  "deliveries.proof.kind.signed_bol": "Signed BOL",
  "deliveries.proof.kind.pallets": "Pallets at drop-off",
  "deliveries.proof.kind.issue": "A problem",
  "deliveries.proof.kind.other": "Other",
  "deliveries.proof.upload": "Upload",
  "deliveries.proof.also": "Also for the trip's other orders",
  "deliveries.proof.uploading": "Uploading…",
  "deliveries.proof.by": "Uploaded by {who}",
  "deliveries.proof.remove": "Remove",
  "deliveries.proof.removeConfirm": "Remove this upload?",
  "deliveries.proof.openPdf": "Open PDF",
  "deliveries.markHint": "Upload the signed BOL or a pallets photo first.",
  "deliveries.podMissing": "Missing",
  "deliveries.flag.no_pod": "No POD",
  "deliveries.flag.pod_far_from_drop": "POD far from drop",
  "deliveries.flag.late": "Late",
  "deliveries.flag.before_window": "Before window",
  "deliveries.flag.skipped": "Skipped",
  "deliveries.flag.issue_reported": "Problem reported",
  "deliveries.flag.no_pod_location": "No POD location",
  "deliveries.flag.route_not_completed": "Route not completed",
  "deliveries.flag.no_carrier_update": "No update from the carrier",
  "deliveries.flag.samsara_disagrees": "Samsara disagrees",
  "deliveries.flag.pod_after_override": "POD after override",
  "deliveries.sum.follow_ups": "Open follow-ups",
  "deliveries.col.followUp": "Follow-up",
  "deliveries.said.samsara": "Samsara",
  "deliveries.said.carrier": "Carrier",
  "deliveries.said.dispatcher": "Dispatcher ✓",
  "deliveries.disp.awaiting_decision": "Awaiting decision",
  "deliveries.disp.redeliver": "Redeliver",
  "deliveries.disp.returning_to_warehouse": "Returning to warehouse",
  "deliveries.disp.at_warehouse": "Back at warehouse",
  "deliveries.disp.customer_pickup": "Customer pick-up",
  "deliveries.disp.picked_up": "Picked up",
  "deliveries.disp.cancelled": "Cancelled",
  "deliveries.filter.code": "Exception",
  "deliveries.filter.source": "Status from",
  "deliveries.followUps": "Follow-ups",
  "deliveries.followUps.empty": "No open follow-ups.",
  "deliveries.followUps.attempt": "Attempt",
  "deliveries.followUps.attemptN": "Attempt {n}",
  "deliveries.followUps.onPlan": "On the plan of {date}",
  "deliveries.outcome.title": "Record outcome",
  "deliveries.outcome.hintSamsara": "Overrides what Samsara reports: say why.",
  "deliveries.outcome.hintCarrier": "What the carrier reported for this order.",
  "deliveries.outcome.code": "Exception",
  "deliveries.outcome.pick": "Pick…",
  "deliveries.outcome.reason": "Why Samsara is wrong",
  "deliveries.outcome.disposition": "Next step",
  "deliveries.outcome.save": "Save",
  "deliveries.outcome.overrides": "Overrides Samsara ({stage})",
  "deliveries.outcome.same": "Samsara already says this: change the exception code instead.",
  "deliveries.outcome.pickCode": "Pick why it failed.",
  "deliveries.outcome.needReason": "Say why Samsara is wrong.",
  "deliveries.outcome.needNote": "{code} needs a note saying what happened.",
  "deliveries.override.title": "Dispatcher's outcome",
  "deliveries.override.by": "Set by {who} at {time}: {reason}",
  "deliveries.override.agrees": "Samsara agrees.",
  "deliveries.override.samsaraSays": "Samsara says: {stage}.",
  "deliveries.override.reason": "Why use Samsara's",
  "deliveries.override.revert": "Use Samsara's",
  "deliveries.followUp.title": "Exception and next step",
  "deliveries.followUp.code": "Exception: {code}",
  "deliveries.followUp.recode": "Change code",
  "deliveries.followUp.now": "Next step: {disposition}",
  "deliveries.followUp.none": "No next step for this order.",
  "deliveries.followUp.note": "Note on the next step",
  "deliveries.followUp.save": "Set next step",
  "deliveries.followUp.noteCancel": "Say why it is cancelled.",
  "deliveries.review.keepHint": "Keep mine: review it with a note. Or use Samsara's below.",
  "deliveries.history.title": "History",
  "deliveries.history.attempt": "Attempt {n} of {m}",
  "deliveries.history.this": "this one",
  "deliveries.history.empty": "Nothing recorded yet.",
  "deliveries.history.kind.samsara_reading": "Samsara reading",
  "deliveries.history.kind.carrier_report": "Carrier report",
  "deliveries.history.kind.trip_reset": "Trip put back",
  "deliveries.history.kind.override": "Override",
  "deliveries.history.kind.revert": "Back to Samsara's",
  "deliveries.history.kind.recode": "Re-coded",
  "deliveries.history.kind.disposition": "Next step",
  "deliveries.history.kind.proof_added": "Proof uploaded",
  "deliveries.history.kind.proof_removed": "Proof removed",
  "deliveries.history.kind.review": "Reviewed",
  "deliveries.carriedBy": "Carried by",
  "deliveries.all": "All",
  "deliveries.ownFleet": "Own fleet",
  "deliveries.anyCarrier": "Any 3PL carrier",
  "deliveries.noCarrier": "No carrier",
  "deliveries.source.samsara": "Samsara",
  "deliveries.source.manual": "Manual",
  "deliveries.unassigned": "3PL truck {truck} ({n} order{s}) has no carrier yet.",
  "deliveries.openPlan": "Open plan",
  "deliveries.update.title": "Carrier update for the trip",
  "deliveries.update.hint":
    "This 3PL carrier isn't on Samsara: record what it reports for the whole trip here, and each order's outcome above.",
  "deliveries.update.stage": "What happened",
  "deliveries.update.at": "At",
  "deliveries.update.note": "Note",
  "deliveries.update.noteHint": "Say what happened in the note.",
  "deliveries.update.scheduledHint":
    "Puts the whole trip back as booked, for entries made by mistake; its booking can then be cancelled.",
  "deliveries.update.trip": "Record for the whole trip",
  "deliveries.export": "Export CSV",
  "deliveries.details": "Details",
  "deliveries.ev.pickedUp": "Left the warehouse {time}",
  "deliveries.ev.enRoute": "Set off toward this drop {time}",
  "deliveries.ev.visit": "Arrived {arrived} · left {departed} ({min} min on site)",
  "deliveries.ev.arrived": "Arrived {time}, still at the drop",
  "deliveries.ev.notReached": "The driver didn't reach this drop",
  "deliveries.ev.notYet": "Not reached yet",
  "deliveries.ev.notTracked": "Samsara didn't record the truck at this drop",
  "deliveries.ev.dispatcherProof": "Proof uploaded by the dispatcher (below)",
  "deliveries.ev.pod": "Proof given {time} at {place}",
  "deliveries.ev.noPlace": "an unknown location",
  "deliveries.ev.podDistance": " · {km} km from the drop",
  "deliveries.ev.noPod": "No proof of delivery",
  "deliveries.ev.reportedAt": "{stage} {time}, as the carrier reported",
  "deliveries.ev.reported": "{stage}, as the carrier reported",
  "deliveries.ev.noReport": "No outcome from the carrier yet",
  "deliveries.ev.problem": "Problem reported: {issue}",
  "deliveries.ev.note": "Driver's note: {note}",
  "deliveries.photoFailed": "Photo unavailable",
  "deliveries.shareLink": "Share link",
  "deliveries.noLink": "No share link yet",
  "deliveries.copyLink": "Copy share link",
  "deliveries.copied": "Copied",
  "deliveries.newLink": "New link",
  "deliveries.newLinkConfirm":
    "Replace this order's share link? Anyone with the old link can no longer open it.",

  // ── Order page (public, opened by a share link) ──
  "order.title": "Order {order}",
  "order.timesIn": "times in {zone}",
  "order.invalid": "This link isn't valid. Please contact ANL for your delivery details.",
  "order.failed": "We couldn't load this delivery. Please try again in a few minutes.",
  "order.status.delivered": "Delivered",
  "order.status.delivery_exception": "Delivery exception – contact ANL",
  "order.status.not_delivered": "Not delivered",
  "order.next.rescheduled": "Delivery attempted; it will be rescheduled.",
  "order.next.redelivery_planned": "Redelivery planned.",
  "order.next.returned_to_warehouse": "Returned to our warehouse.",
  "order.ev.pickedUp": "Left ANL's {depot} warehouse",
  "order.ev.arrived": "Driver arrived",
  "order.ev.left": "Driver left",
  "order.pallets": "{n} pallet{s}",
  "order.pickedUp": "Picked up",
  "order.proof": "Proof of delivery",
  "order.photo.pickup": "Pallets at pick-up",
  "order.photo.pallets": "Pallets at drop-off",
  "order.photo.signed_bol": "Signed bill of lading",
  "order.download": "Download",
  "order.openPdf": "Open PDF",
  "order.footer":
    "Shared by ANL. Times are local to the delivery; photos are stamped with where they were taken.",

  // ── History ──
  "history.title": "Past runs",
  "history.desc":
    "Every optimization is saved. Pick a date, open a run to review its plan and " +
    "inputs, or select two to compare.",
  "history.runDate": "Run date",
  "history.noRuns": "— no runs —",
  "history.compare": "Compare",
  "history.compareReady": "Compare 2 runs",
  "history.comparePartial": "Compare ({n}/2)",
  "history.noRunsDate": "No runs on this date.",
  "history.open": "Open",
  "history.inputBtn": "↓ Input",
  "history.downloadTip":
    "Download the exact input this run was built from (re-uploadable CSV)",
  "history.backToRuns": "← Back to runs",
  "history.downloadInput": "Download input",
  "history.runFailed": "This run failed:",
  "history.inputs": "Inputs",
  "history.source": "source",
  "history.ordersSelected": "{n} orders selected",
  "history.palletsIn": "{n} pallets in",
  "history.fleet": "Fleet: {f26}×26ft · {f53}×53ft · {f3pl} 3PL",
  "history.departures": "Departs {list}",
  "history.compareRuns": "Compare runs",
  "history.runA": "Run A",
  "history.runB": "Run B",
  "history.diffNote": "Highlighted rows differ between the two runs.",

  // ── Dispatch plan ──
  "results.dispatchPlan": "Dispatch plan →",
  "plan.title": "Dispatch plan",
  "plan.meta": "{depot} · version {v}",
  "plan.back": "← Back",
  "plan.loading": "Opening the dispatch plan…",
  "plan.loadFailed": "Couldn't open the dispatch plan:",
  "plan.retry": "Retry",
  "plan.summary": "Plan summary",
  "plan.date": "Plan date",
  "plan.status": "Status",
  "plan.routes": "Routes",
  "plan.stops": "Stops",
  "plan.shipments": "Pallets",
  "plan.vehicles": "Vehicles assigned",
  "plan.drivers": "Drivers assigned",
  "plan.idle": "{n} vehicle{s} idle",
  "plan.idleDrivers": "{n} driver{s} free",
  "plan.validation": "Validation",
  "plan.validationStale": "Out of date — save",
  "plan.validating": "Recalibrating…",
  "plan.notValidated": "Not validated",
  "plan.validationFailedShort": "Couldn't validate",
  "plan.validationCounts": "Blocking: {b} · Warnings: {w}",
  "plan.phase.draft": "Draft",
  "plan.phase.modified": "Modified",
  "plan.phase.validating": "Validating",
  "plan.phase.valid": "Valid",
  "plan.phase.invalid": "Has blocking issues",
  "plan.phase.validation_error": "Validation failed",
  "plan.phase.publishing": "Publishing",
  "plan.phase.published": "Published",
  "plan.phase.partially_published": "Partially published",
  "plan.phase.publish_failed": "Publish failed",
  "plan.phase.closed": "Closed",
  "plan.revalidate": "Save",
  "plan.lastSaved": "Last saved {time}",
  "plan.exportExcel": "Export Excel",
  "plan.exportSaveFirst": "Save first: the workbook is the plan as saved.",
  "plan.savedPlans": "Saved plans",
  "plan.trackDeliveries": "Track in Deliveries",
  "plan.publish": "Publish plan",
  "plan.publishing": "Publishing…",
  "plan.retryFailed": "Retry failed routes",
  "plan.publishDisabledTip": "Save and resolve blocking issues to publish.",
  "plan.discard": "Discard changes",
  "plan.reset": "Reset to optimizer plan",
  "plan.map": "Route map",
  "plan.mapStale": "Map out of date — save to redraw your changes.",
  "plan.threeplLane": "3PL lane {n}",
  "plan.carrier3pl": "3PL carrier",
  "plan.announceTruck3pl": "Truck {truck}: all its trips go to the 3PL carrier.",
  "plan.laneTimes": "Departs {depart} · last drop {last} · {mi} mi · ${cost} at 3PL rates",
  "plan.with3pl": "{fleet} + {n} 3PL",
  "plan.threeplCost": "3PL cost",
  "plan.direct3pl": "3PL direct (oversize)",
  "plan.direct3plNote":
    "Too big for any of our trucks: each ships alone by 3PL. Shown for the record; not editable.",
  "plan.customer": "Customer",
  "plan.handedTo3pl": "Handed to 3PL, not sent to Samsara: {routes}.",
  "plan.loading.sequenced": "Stops ordered for the 3PL lane",
  "plan.locked": "Stop order set by you",
  "plan.unlock": "Let optimizer order stops",
  "plan.announceUnlocked": "{route}: the optimizer may order the stops again.",
  "plan.resequenced": "Stops re-ordered by recalibration",
  "plan.loading.verified": "Loading checked",
  "plan.loading.order_unverified": "Pallets fit; loading for your stop order not checked",
  "plan.loading.not_verified": "Loading not checked in time",
  "plan.loading.infeasible": "Pallets can't all be loaded",
  "plan.issue.loading_infeasible": "{route}: these pallets can't all be loaded in this trailer.",
  "plan.issue.loading_not_verified": "{route}: loading couldn't be checked in time.",
  "plan.issue.loading_order_unverified":
    "{route}: the pallets fit, but loading for your stop order wasn't checked.",
  "plan.resetConfirm":
    "Replace this plan with the optimizer's original plan? Every change, saved or not, will be discarded.",
  "plan.announceReset": "The plan is back to the optimizer's plan.",
  "plan.atBaselineTip": "This is already the optimizer's plan.",
  "plan.discardConfirm": "Discard your unsaved changes to this dispatch plan?",
  "plan.conflict":
    "This plan changed elsewhere ({message}). Reload the latest version — your unsaved changes will be discarded.",
  "plan.reloadLatest": "Reload latest",
  "plan.refresh": "Refresh plan",
  "plan.rejected": "The server rejected these changes:",
  "plan.requestFailed": "Something went wrong: {message}",
  "plan.publishedBanner":
    "Published {at} by {by}. Routes were handed to the Samsara publisher (simulated until the integration is live).",
  "plan.partialBanner": "{sent} of {total} routes reached drivers. Retry the rest:",
  "plan.partialBannerStarted": "{sent} of {total} routes reached drivers. A retry won't change these:",
  "plan.close": "Close plan",
  "plan.refreshDelivery": "Refresh delivery status",
  "plan.deliveryAsOf": "Delivery status as of {time}",
  "plan.announceDelivery": "Delivery status refreshed.",
  "plan.delivery.scheduled": "Not reached yet",
  "plan.delivery.unassigned": "Not reached yet",
  "plan.delivery.en_route": "On the way",
  "plan.delivery.arrived": "Arrived {arrived}",
  "plan.delivery.departed": "Arrived {arrived} · Left {departed}",
  "plan.delivery.skipped": "Skipped",
  "plan.pod.not_started": "Proof of delivery not started",
  "plan.pod.in_progress": "Proof of delivery in progress",
  "plan.pod.submitted": "Proof of delivery submitted",
  "plan.pod.archived": "Proof of delivery archived",
  "plan.podPhotos": "Proof-of-delivery photos",
  "plan.photoProcessing": "{label} (processing)",
  "plan.closeConfirm":
    "Close this plan as it stands? Routes already sent stay with their drivers; the rest won't be sent, and the plan can't be edited or retried.",
  "plan.closedBanner":
    "Closed {at} by {by}. {sent} of {total} routes reached drivers; the rest weren't sent.",
  "plan.unknownError": "unknown error",
  "plan.stuckBanner":
    "Publishing was interrupted. It's safe to retry — routes already sent won't be sent again.",
  "plan.inProgressBanner": "This plan is being published…",
  "plan.validationFailed": "The routes couldn't be validated: {message}",
  "plan.trip": "Trip {n}",
  "plan.routeTimes": "Loads {start} · departs {depart} · back {back} · {h} h · {mi} mi",
  // A fleet trip's cost at our rates; plans validated before costs existed have none.
  "plan.routeCost": "${cost}",
  "plan.noTiming": "Not timed yet — save",
  "plan.emptyRoute": "No stops — this route won't be published",
  "plan.staleShort": "out of date",
  "plan.vehicle": "Vehicle",
  "plan.driver": "Driver",
  "plan.unassigned": "Unassigned",
  "plan.offToday": "off today",
  "plan.routeSent": "Sent to driver",
  "plan.routeFailed": "Not sent: {error}",
  "plan.routeNotSent3pl": "Not sent: handled by 3PL",
  "plan.nothingToPublish": "Nothing to publish: no fleet route has any orders.",
  "plan.weight": "Weight",
  "plan.dueBy": "Due by {time}",
  "plan.notBefore": "Not before {time}",
  "plan.no53": "No 53′ access",
  "plan.orderAria": "Order {order}: {name}",
  "plan.moveEarlier": "Move {order} earlier",
  "plan.moveLater": "Move {order} later",
  "plan.announceEarlier": "{order} moved earlier.",
  "plan.announceLater": "{order} moved later.",
  "plan.announceMoved": "Stop {zip} moved to position {pos} on {route}.",
  "plan.announceMovedOrder": "Order {order} moved to {route}.",
  "plan.announceUnload": "Order {order} now unloads in a new position at its stop.",
  "plan.announceVehicle": "{route}: vehicle set to {v}.",
  "plan.announceDriver": "{route}: driver set to {d}.",
  "plan.announceDeparture": "{truck}: leaves at {at}.",
  "plan.announceValidated": "Validated. Blocking: {b}, warnings: {w}.",
  "plan.validationTitle": "Validation",
  "plan.staleNote":
    "You have unsaved changes. Save to keep them and re-plan the trips.",
  "plan.sev.blocking": "Blocking — fix before publishing",
  "plan.sev.warning": "Warnings — acknowledge to publish",
  "plan.sev.info": "Notes",
  "plan.noIssues": "No problems found.",
  "plan.route": "Route",
  "plan.zipReason.no_street": "No street in the order's destination details: the driver gets the ZIP area.",
  "plan.zipReason.self_pickup": "Marked as a customer pickup.",
  "plan.zipReason.zip_mismatch": "The address names another ZIP: check the order.",
  "plan.zipReason.not_geocoded": "Not placed on the map yet: the next sync looks this address up.",
  "plan.zipReason.no_match": "No street-level match for this address in its ZIP.",
  "plan.zipReason.unknown": "No street address on file: the driver gets the ZIP area.",
  "plan.zipReason.unplaced": "This address isn't placed on the map: the driver gets the ZIP area.",
  "plan.lateBy": "{m} min late",
  "plan.pallet": "Pallet",
  "plan.dims": "Size (L×W×H)",
  "plan.publishTitle": "Publish {n} route{s} to drivers?",
  "plan.retryTitle": "Retry {n} route{s}?",
  "plan.publishDesc":
    "Each driver gets their route for {date} in the Samsara app. You can still change the plan afterwards and publish the changes.",
  "plan.departs": "Departs",
  "plan.warningsToAck": "{n} warning{s} to review",
  "plan.ackWarnings": "I've reviewed these warnings and want to publish anyway.",
  "plan.publishNote":
    "Samsara isn't connected yet — publishing is simulated, and records who published and when.",
  "plan.publishNoteLive":
    "Each trip goes to its truck in Samsara, which tracks it by the truck's GPS whoever is signed in.",
  "plan.cancel": "Cancel",
  "plan.confirmPublish": "Publish",
  "plan.confirmRetry": "Retry",
  "plan.publishChanges": "Publish changes",
  "plan.noChangesTip": "No changes to send: drivers have the plan as it is.",
  "plan.changesTitle": "Publish changes to {n} route{s}?",
  "plan.changesDesc":
    "Drivers' Samsara apps are updated to match the plan. A route whose driver has already started stays as it is.",
  "plan.changesNew": "New routes",
  "plan.changesUpdated": "Updated routes",
  "plan.changesRemoved": "Removed from drivers",
  "plan.changesPending":
    "Changes not sent yet: drivers have the plan as it was last published. Publish the changes to update their apps.",
  "plan.routeChanged": "Changed — not sent to its driver yet",
  "plan.routeToWithdraw": "Will be removed from its driver's app",
  "plan.routeStarted": "Its driver has started this route, so it can't be changed here.",
  "plan.routeWithdrawn": "Cleared from Samsara",
  "plan.routeKeptStarted": "Left as it was: its driver had already started it",
  "plan.routeUpdateFailed": "Its driver still has the last version sent: {error}",
  "plan.withdraw": "Clear routes from Samsara",
  "plan.withdrawTitle": "Clear {n} route{s} from Samsara?",
  "plan.withdrawDesc":
    "Each route below is deleted from its driver's app. A route whose driver has already started stays as it is. The plan goes back to draft with your edits kept, so you can change it and publish again.",
  "plan.withdrawNote": "Samsara isn't connected yet — clearing is simulated.",
  "plan.withdrawNoteLive": "The routes are deleted in Samsara now, and their drivers stop seeing them.",
  "plan.confirmWithdraw": "Clear routes",
  "plan.withdrawing": "Clearing…",
  "plan.announceWithdrawn": "Routes cleared from Samsara.",
  "plan.issue.vehicle_missing": "{route} has no vehicle.",
  "plan.issue.vehicle_unknown": "{route}: vehicle {vehicle_id} isn't in the fleet.",
  "plan.issue.driver_missing": "{route} has no driver.",
  "plan.issue.driver_unknown": "{route}: driver {driver_id} isn't on the roster.",
  "plan.issue.driver_unavailable": "{route}: {driver} isn't available today.",
  "plan.issue.driver_multiple_vehicles": "{route}: {driver} is assigned to more than one vehicle.",
  "plan.issue.vehicle_multiple_trucks": "{route}: {vehicle} is on more than one truck.",
  "plan.issue.no_53ft_access": "{route}: ZIP {zip} can't take a 53′ trailer.",
  "plan.issue.pallet_does_not_fit":
    "{route}: pallet {shipment_id} doesn't fit a {trailer_type} trailer.",
  "plan.issue.plan_date_past": "The plan date {plan_date} has passed.",
  "plan.issue.another_plan_published":
    "Another plan for {plan_date} from this depot is already published — drivers may get duplicate routes.",
  "plan.issue.address_missing":
    "{n} drop{s} without a street-level location — drivers get the ZIP area and arrival tracking is approximate.",
  "plan.issue.address_outside_area":
    "{route}: order {order_no}'s address is in {address_zip}, outside this warehouse's area — it's planned for the order's ZIP, {zip}.",
  "plan.issue.customer_pickup":
    "{route}: order {order_no} is marked as a customer pickup — check it belongs on a truck.",
  "plan.issue.route_empty": "{route} has no stops and won't be published.",
  "plan.issue.excluded_shipments":
    "{n} pallet{s} go by 3PL or couldn't be placed — not part of this dispatch.",
  "plan.issue.overweight": "{route} is {weight_over_kg} kg over the trailer's weight limit.",
  "plan.issue.over_floor_area": "{route} needs {footprint_pct}% of the trailer floor area.",
  "plan.issue.late": "{route}: {order_no} arrives {eta}, {late_min} min after its {deadline} deadline.",
  "plan.issue.early": "{route}: {order_no} arrives {eta}, before its {after} window opens.",
  "plan.issue.driver_hours": "{driver} works {hours} h today (over {limit} h).",
  "plan.issue.departure_passed":
    "{route} was due to leave at {depart}: sent now, it starts at once and may end with its drops skipped.",
  // ── Dispatch plan: the table, and the dispatcher's own orders and routes ──
  "plan.tableLabel": "Orders by route",
  "plan.col.stop": "Stop",
  "plan.col.order": "Order",
  "plan.col.customer": "Customer",
  "plan.col.zip": "ZIP",
  "plan.col.pallets": "Pallets",
  "plan.col.weight": "Weight",
  "plan.col.window": "Window",
  "plan.col.eta": "ETA",
  "plan.col.address": "Address",
  "plan.col.notes": "Notes",
  "plan.col.status": "Delivery status",
  "plan.col.actions": "Actions",
  "plan.stopNumberFor": "Stop number for ZIP {zip}",
  "plan.routeFor": "Route for order {order}",
  "plan.takeOff": "Take {order} off the plan",
  "plan.showPallets": "Show pallets of {order}",
  "plan.hidePallets": "Hide pallets of {order}",
  "plan.palletsOf": "Pallets of {order}",
  "plan.zipOnlyShort": "ZIP {zip} only",
  "plan.badgeAdded": "Added",
  "plan.badgeManual": "Manual",
  "plan.addedRoute": "Added route {n}",
  "plan.truck": "Truck {truck}",
  "plan.truckTrips": "{n} trip{s}",
  "plan.addTruck": "Add truck",
  "plan.addTrip": "Add trip",
  "plan.addTripTo": "Add a trip to {truck}",
  "plan.removeTrip": "Remove",
  "plan.removeTripOf": "Remove {route}",
  "plan.removeTruck": "Remove truck",
  "plan.removeTruckOf": "Remove truck {truck}",
  "plan.lunchAt": "lunch {time}",
  "plan.zipOf": "ZIP {zip}",
  "plan.announceTripAdded": "{route} added.",
  "plan.announceTruckAdded": "Truck {truck} added: pick its vehicle.",
  "plan.announceTripRemoved": "{route} removed.",
  "plan.announceTruckRemoved": "Truck {truck} removed.",
  "plan.loadPct": "load {w}% of weight · {a}% of floor",
  "plan.emptyRouteRow": "No orders yet: pick this route on an order, drag one here, or add orders.",
  "plan.addOrders": "Add orders",
  "plan.addOrdersTo": "Add orders to {route}",
  "plan.unroutedTitle": "Not on a route",
  "plan.unroutedNote":
    "Taken off the plan: not delivered on it or sent to drivers. Put an order back on a route to deliver it.",
  "plan.putBackOn": "Put {order} back on",
  "plan.putBackPlaceholder": "Put back on…",
  "plan.announceTakenOff": "Order {order} taken off the plan.",
  "plan.announcePutBack": "Order {order} put back on {route}.",
  "plan.announceAdded": "{n} order{s} added and recalibrated.",
  "plan.resetAddedOrders": "{n} order{s}",
  "plan.resetAddedRoutes": "{n} trip{s}",
  "plan.resetConfirmAdded": "It also takes off the {what} you added.",
  "plan.and": " and ",
  "plan.editAddress": "Edit address of {order}",
  "plan.addressOf": "Address of {order}",
  "plan.addressSave": "Save",
  "plan.useSystemAddress": "Use the system's address",
  "plan.addressEdited": "Edited",
  "plan.systemAddress": "System: {address}",
  "plan.addressMovedZip": "Planned for ZIP {zip}, where this address is (the order says {own}).",
  "plan.addressUnsaved": "Save to place this address on the map.",
  "plan.announceAddress": "Address of {order} changed.",
  "plan.announceAddressBack": "Address of {order} back to the system's.",
  "plan.editNotes": "Edit notes of {order}",
  "plan.notesOf": "Notes of {order}",
  "plan.notesSave": "Save notes",
  "plan.notesTooLong": "Notes can be at most {max} characters (these are {n}).",
  "plan.notesEdited": "Edited",
  "plan.systemNotes": "System: {notes}",
  "plan.noNotes": "No notes",
  "plan.useSystemNotes": "Use the system's notes",
  "plan.announceNotes": "Notes of {order} changed.",
  "plan.announceNotesBack": "Notes of {order} back to the system's.",
  "plan.editWindow": "Edit window of {order}",
  "plan.windowEdited": "Edited",
  "plan.windowAfter": "Not before",
  "plan.windowDeadline": "Deliver by",
  "plan.windowAfterOf": "Not before, {order}",
  "plan.windowDeadlineOf": "Deliver by, {order}",
  "plan.windowSave": "Save window",
  "plan.windowBackwards": "“Not before” must be earlier than “Deliver by”.",
  "plan.windowUnsaved": "Save to re-time the route with this window.",
  "plan.systemWindow": "System: {window}",
  "plan.systemNotBefore": "not before {time}",
  "plan.systemDueBy": "due by {time}",
  "plan.noWindow": "no window",
  "plan.useSystemWindow": "Use the system's window",
  "plan.announceWindow": "Window of {order} changed.",
  "plan.announceWindowBack": "Window of {order} back to the system's.",

  // ── Dispatch plan: the order selector ──
  "add.title": "Add orders to the plan",
  "add.desc":
    "Pick orders from the order sync, or enter one it doesn't have. Each goes on the route you choose and is recalibrated there; nothing is optimized again.",
  "add.fromSync": "Orders in the sync",
  "add.search": "Search order or customer",
  "add.searchBtn": "Search",
  "add.loading": "Loading orders…",
  "add.loadFailed": "Couldn't load orders: {message}",
  "add.none": "No orders found.",
  "add.onPlan": "On this plan: {where}",
  "add.allRegions": "All warehouses",
  "add.picked": "Picked",
  "add.add": "Add {order}",
  "add.paste": "Paste order numbers",
  "add.find": "Find",
  "add.notFound": "Not found: {list}",
  "add.alreadyOn": "Already on this plan: {list}",
  "add.manualLegend": "An order the sync doesn't have",
  "add.orderNo": "Order number",
  "add.customer": "Customer",
  "add.zip": "ZIP",
  "add.address": "Address",
  "add.notes": "Notes for the driver",
  "add.addManual": "Add manual order",
  "add.toAdd": "To add",
  "add.nothingPicked": "Nothing picked yet.",
  "add.remove": "Remove {order}",
  "add.route": "Route",
  "add.notBefore": "Not before",
  "add.dueBy": "Due by",
  "add.anyTime": "Any time",
  "add.length_cm": "Length (cm) of pallet {n}",
  "add.width_cm": "Width (cm) of pallet {n}",
  "add.height_cm": "Height (cm) of pallet {n}",
  "add.weight_kg": "Weight (kg) of pallet {n}",
  "add.palletUnits": "Each pallet: length × width × height (cm), weight (kg)",
  "add.addPallet": "Add a pallet",
  "add.removePallet": "Remove pallet {n}",
  "add.loadingPallets": "Loading pallets…",
  "add.confirm": "Add {n} order{s}",
  "add.adding": "Adding…",
  "add.unrouted": "Not on a trip yet",
  // Hand-built plans (issue 46)
  "app.creatingPlan": "Creating the plan…",
  "picker.planByHand": "Plan {n} order{s} by hand",
  "picker.planByHandTip": "Make a plan from these orders without running the optimizer.",
  "picker.planByHandTitle": "Plan by hand",
  "picker.planByHandTrips":
    "The plan starts with the trucks and trips you set here, the orders in the order you listed them. Change anything on the plan page.",
  "picker.planByHandPool":
    "The plan starts with these orders waiting for a trip. Add trucks and put the orders on their trips on the plan page.",
  "picker.planByHandOnPlans":
    "Already on a plan: {n} of these orders — the plan page warns before you publish.",
  "picker.planDate": "Delivery day",
  "picker.cancel": "Cancel",
  "picker.createPlan": "Create plan",
  "picker.openPlanAt": "Open the plan this order is on",
  "picker.statusCol": "Status",
  "picker.statusFilter": "Status",
  "picker.statusAll": "All",
  "picker.statusFilter.available": "Available",
  "picker.statusFilter.on_plan": "On a plan",
  "picker.statusFilter.delivered": "Delivered",
  "picker.statusFilter.exception": "Exception",
  "status.available": "Available",
  "status.planned": "Planned",
  "status.scheduled": "Scheduled",
  "status.left_depot": "Left depot",
  "status.at_drop": "At drop",
  "status.delivered": "Delivered",
  "status.delivery_exception": "Delivery exception",
  "status.not_delivered": "Not delivered",
  "plan.originHand": "Built by hand",
  "plan.originRun": "From an optimizer run",
  "plan.startOver": "Start over",
  "plan.startOverConfirm":
    "Go back to the orders this plan was made with, none of them on a trip? Every change, saved or not, is discarded.",
  "plan.noTrucksYet": "No trucks yet. Add a truck, then put orders on its trip.",
  "plan.noTrucks": "No trucks on this plan.",
  "plan.unroutedPoolNote":
    "Waiting for a trip: not delivered with this plan until they're on one. Pick orders and put them on a trip.",
  "plan.pickAllUnrouted": "Pick every waiting order",
  "plan.pickUnrouted": "Pick {order}",
  "plan.putOnTrip": "Put picked orders on a trip",
  "plan.putOnTripN": "Put {n} on a trip…",
  "plan.announcePutOnTrip": "{n} order{s} put on {route}.",
  "plan.openOtherPlan": "Open that plan",
  "plan.sequencePlanned": "Stop order planned on Save",
  "picker.onPlansNote": "Already on a plan: {n} of these orders.",
  "plan.issue.orders_unrouted": "{n} order{s} aren't on a trip and won't be delivered with this plan.",
  "plan.issue.order_on_another_plan":
    "{route}: order {order_no} is also on a trip of another plan for this day — it may be delivered twice.",
  "plan.issue.carrier_unassigned": "3PL truck {truck_id} has no carrier asked or booked yet.",
  "deliveries.searchLabel": "Find an order",
  "deliveries.searchHint": "Order number, pallet id or uShipment order id",
  "deliveries.search": "Search",
  "deliveries.clearSearch": "Back to the day",
  "deliveries.searchFailed": "Couldn't search: {msg}",
  "deliveries.searchResults": "Search results",
  "deliveries.notFound": "Not on any of your plans.",
  "deliveries.notFoundAnywhere": "Not on any of your plans, and not in the order sync.",
  "deliveries.group": "Progress",
  "deliveries.group.pending": "Pending",
  "deliveries.group.in_transit": "In transit",
  "deliveries.group.completed": "Completed",
  "deliveries.group.exception": "Exception",
} as const;

export type MsgKey = keyof typeof en;

const zh: Record<MsgKey, string> = {
  "app.brand": "排车规划",
  "app.signOut": "退出登录",
  "app.loading": "加载中…",
  "app.submitting": "提交中…",
  "app.optimizing": "优化中…",
  "app.tabOrders": "从仓库选单",
  "app.tabUpload": "上传 CSV",
  "app.tabHistory": "历史记录",
  "app.tabDeliveries": "送达记录",
  "app.tabCosts": "3PL 费用",
  "costs.title": "3PL 费用",
  "costs.desc": "每个已订 3PL 车次的议定费用与发票费用。录入承运商发票，与议定价核对后对账。每个车次的费用按各站点增加的里程分摊到订单；对账时分摊随之锁定。",
  "costs.from": "从",
  "costs.to": "至",
  "costs.carrier": "承运商",
  "costs.allCarriers": "全部承运商",
  "costs.statusFilter": "费用状态",
  "costs.allStatuses": "全部",
  "costs.status.open": "未开票",
  "costs.status.invoiced": "已开票",
  "costs.status.reconciled": "已对账",
  "costs.export": "导出 CSV",
  "costs.exportOrders": "按订单导出",
  "costs.col.allocation": "分摊",
  "costs.alloc.state.frozen": "已锁定",
  "costs.alloc.state.provisional": "暂定",
  "costs.alloc.state.cancelled": "不分摊（已取消）",
  "costs.alloc.state.no_orders": "不分摊（无订单）",
  "costs.alloc.state.no_cost": "尚无费用",
  "costs.alloc.changed": "分摊后车次已变",
  "costs.alloc.estimateOnly": "未录入费用：仅有估算",
  "costs.alloc.unallocated": "此已取消订舱上的 {usd} 费用未分摊到任何订单。",
  "costs.alloc.title": "按订单分摊",
  "costs.alloc.basis.reconciled": "对账金额",
  "costs.alloc.basis.invoiced": "发票合计",
  "costs.alloc.basis.negotiated": "议定合计",
  "costs.alloc.basis.estimate": "模型估算",
  "costs.alloc.splitting": "按各站点增加的里程分摊{basis} {usd}。",
  "costs.alloc.frozenBy": "{who} 于 {when} 对账时锁定。",
  "costs.alloc.changedHint": "对账后该车的行程或订单有变。重新打开并再次对账，即按当前情况分摊。",
  "costs.alloc.provisionalHint": "暂定：对账前随计划和费用变化。",
  "costs.alloc.trip": "第 {n} 趟 · {route}",
  "costs.alloc.open": "止于最后一站",
  "costs.alloc.loop": "返回仓库",
  "costs.alloc.sampled": "抽样计算（站点多）",
  "costs.alloc.stop": "站点",
  "costs.alloc.zip": "邮编",
  "costs.alloc.miles": "里程份额",
  "costs.alloc.share": "占比",
  "costs.alloc.order": "订单",
  "costs.alloc.pallets": "托盘",
  "costs.alloc.amount": "金额",
  "costs.alloc.total": "已分摊 {allocated}，共 {total}",
  "costs.alloc.freezeHint": "对账会锁定费用及其按订单的分摊。",
  "costs.loadFailed": "无法加载费用：{msg}",
  "costs.empty": "这段日期没有已订的 3PL 车次。",
  "costs.col.date": "日期",
  "costs.col.truck": "卡车",
  "costs.col.carrier": "承运商",
  "costs.col.orders": "订单",
  "costs.col.negotiated": "议定",
  "costs.col.invoiced": "发票",
  "costs.col.variance": "差额",
  "costs.col.status": "状态",
  "costs.col.warnings": "提醒",
  "costs.cancelled": "订车已取消",
  "costs.details": "详情",
  "costs.lines": "费用明细",
  "costs.basis.negotiated": "议定",
  "costs.basis.invoiced": "发票",
  "costs.voided": "已作废：{reason}",
  "costs.invoices": "发票",
  "costs.invoiceLine": "{no} · {date} · {total}",
  "costs.invoiceVoided": "{who} 已作废：{reason}",
  "costs.voidInvoice": "作废发票",
  "costs.voidReason": "作废原因？发票明细会一起作废。",
  "costs.deliveries": "{orders} 单中已送达 {delivered} 单",
  "costs.warn.pod_missing": "已送达但无签收：{orders}",
  "costs.warn.not_delivered": "未送达：{orders}",
  "costs.warn.no_outcome": "未记录结果：{orders}",
  "costs.reconcile": "按 {usd} 对账",
  "costs.reconciled": "{who} 已按 {usd} 对账",
  "costs.reopen": "重新打开",
  "costs.reopenReason": "重新打开的原因？",
  "costs.documents": "单据",
  "costs.noDocuments": "暂无单据。",
  "costs.doc.rate_confirmation": "运价确认单",
  "costs.doc.invoice": "发票",
  "costs.doc.other": "其他",
  "costs.doc.kind": "单据类型",
  "costs.doc.file": "单据文件",
  "costs.doc.upload": "上传",
  "costs.doc.remove": "移除",
  "costs.doc.removeConfirm": "移除此单据？会保留移除记录。",
  "costs.history": "记录",
  "costs.openPlan": "打开计划",
  "costs.inv.open": "录入发票",
  "costs.inv.title": "承运商发票",
  "costs.inv.number": "发票号",
  "costs.inv.date": "开票日期",
  "costs.inv.serviceDate": "服务日期",
  "costs.inv.total": "发票总额",
  "costs.inv.note": "备注",
  "costs.inv.truck": "车次",
  "costs.inv.pickTruck": "选择车次",
  "costs.inv.kind": "类型",
  "costs.inv.code": "代码（如 detention）",
  "costs.inv.amount": "金额",
  "costs.inv.addLine": "添加一行",
  "costs.inv.removeLine": "删除此行",
  "costs.inv.sum": "明细合计 {sum}，发票总额 {total}",
  "costs.inv.save": "保存发票",
  "costs.inv.cancel": "取消",
  "app.tabPlans": "派车计划",

  // ── 已保存的派车计划 ──
  "plans.title": "派车计划",
  "plans.desc": "你保存的派车计划，按计划日期从新到旧排列。打开即可继续编辑、发布或导出。",
  "plans.dateFrom": "计划日期起",
  "plans.dateTo": "计划日期止",
  "plans.table": "已保存的派车计划",
  "plans.col.date": "计划日期",
  "plans.col.warehouse": "仓库",
  "plans.col.status": "状态",
  "plans.col.size": "卡车与订单",
  "plans.col.saved": "上次保存",
  "plans.col.actions": "",
  "plans.trucks": "{n} 辆卡车",
  "plans.orders": "{n} 个订单",
  "plans.unrouted": "{n} 个未排线路",
  "plans.threeplUnassigned": "{n} 辆 3PL 卡车未指派承运商",
  "plans.open": "打开",
  "plans.openFor": "打开 {date} 的计划",
  "plans.empty": "还没有保存的计划。打开某次运行的派车计划即可开始。",
  "plans.loadFailed": "无法加载计划：",

  "login.subtitle": "登录以继续",
  "login.username": "用户名",
  "login.password": "密码",
  "login.signIn": "登录",
  "login.signingIn": "登录中…",

  "picker.heading": "选择订单",
  "picker.desc":
    "已到仓订单列表。勾选要配送的订单或手动添加，所选订单汇总到右侧订单创建区；可设置配送时间窗口并确认每个托盘的尺寸。",
  "picker.arrivedWithin": "到仓时间",
  "picker.allTime": "不限",
  "picker.regionLabel": "仓库",
  "picker.regionAll": "全部仓库",
  "region.socal": "南加州",
  "region.east": "新泽西 / 纽约 / 宾夕法尼亚",
  "region.none": "不在服务范围内",
  "picker.daysN": "{d} 天",
  "picker.searchLabel": "搜索订单号 / 客户",
  "picker.searchPlaceholder": "例如 AN2603…",
  "picker.search": "搜索",
  "picker.viewAll": "全部到仓订单",
  "picker.viewSelected": "已选 ({n})",
  "picker.emptyArrivals": "该时间段内暂无到仓订单",
  "picker.emptySelected": "尚未选择订单",
  "picker.loadingOrders": "正在加载订单…",
  "picker.coldStartHint": "首次加载约需 10–15 秒（服务器正在唤醒）",
  "picker.legendMeasured": "= 真实纸箱尺寸",
  "picker.legendEstimated": "= 按总体积估算（无逐托盘测量，请在右侧确认或修改）",
  "picker.orderBuilder": "订单创建区",
  "picker.modeOptimize": "智能排车",
  "picker.modeEvaluate": "评估我的方案",
  "picker.clearAll": "清空",
  "picker.clearConfirm": "清空订单创建区中的所有订单？",
  "picker.evalInfo":
    "为每个订单指定趟次和车辆：同一趟次的订单一起配送，同一车辆的多趟依次执行。我们会显示每趟在哪些方面超出限制，而非自动优化。",
  "picker.saveDraft": "保存草稿",
  "picker.draftsN": "草稿 ({n}) ▾",
  "picker.load": "载入",
  "picker.noDrafts": "暂无草稿",
  "picker.saveDraftTip": "将当前订单保存为草稿",
  "picker.draftsUnavailableTip": "草稿不可用 —— 后端未配置存储",
  "picker.addManual": "+ 手动添加订单",
  "picker.pasteOpen": "− 粘贴订单号",
  "picker.pasteClosed": "+ 粘贴订单号",
  "picker.emptyCart": "勾选左侧订单，或手动添加",
  "picker.pastePlaceholder": "粘贴订单号 —— 可用换行、逗号或空格分隔",
  "picker.addOrders": "添加订单",
  "picker.adding": "添加中…",
  "picker.pasteNothing": "没有可添加的订单 —— 请先粘贴订单号",
  "picker.pasteResult": "已添加 {added} · 已在列表 {already} · 未找到 {missing}",
  "picker.pasteNotFound": "未找到（请检查拼写）：{list}",
  "picker.manualPlaceholder": "订单号 / 备注",
  "picker.manualBadge": "手动",
  "picker.unnamed": "(未命名)",
  "picker.trip": "趟次",
  "picker.truck": "车辆",
  "picker.deliverBy": "最晚送达",
  "picker.notBefore": "最早送达",
  "picker.timeAny": "不限",
  "picker.palletUnits": "长 × 宽 × 高（厘米）· 重量（千克）",
  "picker.edited": "已修改",
  "picker.validationZip": "订单“{name}”需填写目的地邮编",
  "picker.buildPlan": "生成排车方案（{n} 个订单）",
  "picker.evaluatePlan": "评估方案（{n} 趟）",
  "picker.evaluateTip": "按你的趟次/车辆安排原样运行，查看超限之处",
  "picker.cartOrders": "{n} 个订单",
  "picker.cartPallets": "{n} 个托盘",
  "picker.truckTripShort": "车 {truck} · 趟 {trip}",
  "picker.loadingPallets": "正在加载托盘…",
  "picker.estimatedDimsWarning":
    "系统中没有该订单的尺寸数据——下方托盘为默认尺寸估算（120×100），请核实并填写实际尺寸。",
  "picker.addPallet": "+ 添加托盘",
  "picker.removeOrderTip": "移除",
  "picker.removePalletTip": "移除托盘",
  "picker.draftDeleteTip": "删除草稿",
  "picker.draftNamePrompt": "为草稿命名",
  "picker.draftDeleteConfirm": "删除此草稿？",

  "fleet.26ft": "26 英尺",
  "fleet.53ft": "53 英尺",
  "fleet.available26": "可用 26 英尺",
  "fleet.available53": "可用 53 英尺",
  "fleet.available3pl": "第三方卡车",
  "fleet.truckOf": "{truck} · {size}",
  "fleet.departsOf": "{truck} 出发时间",

  "upload.heading": "规划明日排车",
  "upload.desc": "上传当日发货 CSV。系统将托盘分配到车队、规划每条路线并排布每辆挂车。",
  "upload.drop": "将发货 CSV 拖到此处，或点击选择",
  "upload.cols":
    "必填：订单号、托盘尺寸、重量、目的地邮编 · 可选：截止时间、到货窗口、配送分组、trip_id、truck_id",
  "upload.fleet26": "可用 26 英尺车",
  "upload.fleet53": "可用 53 英尺车",
  "upload.fleet3pl": "第三方卡车",
  "upload.build": "生成排车方案",
  "upload.evaluate": "按原方案评估",
  "upload.evaluateTip": "按 CSV 的 trip_id 分组原样运行，显示每趟超限之处",
  "upload.evaluateExplainer":
    "按原方案评估 按你的分配列运行 —— trip_id（同值订单同趟）和可选 truck_id（同车多趟依次执行）—— 并标出每趟在重量、容量、工时或时间窗口上的超限。",

  "upload.checking": "正在检查文件…",
  "upload.blockers": "有 {n} 板无法用当前车队运送。可删除、增加更大的挂车，或照常排车（这些板会列为未装载）。",
  "upload.warnings": "疑似录入错误 —— 有 {n} 行需要核对：",

  "progress.building": "正在生成排车方案",
  "progress.elapsed": "已用时",
  "progress.ofBudget": "／最长 {budget}",
  "progress.stageDistances": "测算路程",
  "progress.stageAssign": "分配板位到车辆",
  "progress.stageRoute": "排定每趟顺序",
  "progress.stageFinalize": "核算方案成本",
  "progress.retry": "第 {n} 次优化中",
  "progress.hint": "订单较多时耗时更长 —— 系统会持续优化路线，直到用尽时间预算。",

  "results.titlePlan": "排车方案",
  "results.titleEval": "客户方案检查",
  "results.evalSubtitle": "你的趟次方案，原样运行 · ",
  "results.meta": "{p} 个托盘 · {mi} mi · {h} h",
  "results.print": "打印司机派车单",
  "results.newPlan": "← 新方案",
  "results.routeMap": "路线地图",
  "results.pnl": "盈亏",
  "results.threepl": "第三方路线",
  "results.truckDetails": "车辆明细",

  "summary.netProfit": "净利润",
  "summary.pallets": "托盘数",
  "summary.threepl": "3PL",
  "summary.miles": "里程",
  "summary.hours": "工时",
  "summary.revMinusCost": "收入 {rev} − 成本 {cost}",
  "summary.onOwnFleet": "{n} 个走自有车队",
  "summary.routedSolo": "{routed} 拼车 · {solo} 单独",
  "summary.no3pl": "无需第三方",
  "summary.totalDistance": "总行驶里程",
  "summary.driverHourTotal": "司机总工时",
  "summary.fleetSummary": "车队汇总",
  "summary.totalPallets": "托盘总数",
  "summary.ownTruck": "自有车辆",
  "summary.threeplRoutes": "第三方路线",
  "summary.threeplRoutesVal": "{p} 个托盘 / {r} 条路线",
  "summary.threeplSolo": "第三方单独（超大）",
  "summary.unassigned": "未分配",
  "summary.ownFleetTrips": "自有车队趟次",
  "summary.totalMiles": "总里程",
  "summary.totalHours": "总工时",
  "summary.perTruck": "按车辆",
  "summary.pnlTitle": "盈亏",
  "summary.revenue": "收入",
  "summary.fixedCost": "固定成本（沉没）",
  "summary.driverCost": "司机成本",
  "summary.fuelCost": "燃油成本",
  "summary.threeplRoutesCost": "第三方路线成本",
  "summary.threeplSoloCost": "第三方单独成本",
  "summary.totalCost": "总成本",
  "summary.alertBadPlan": "无法生成可用方案（状态：{status}）。请检查发货数据后重试。",
  "summary.alertInfeasible": "{n} 个托盘无法装上任何车辆：",
  "summary.alertSolo3pl":
    "{n} 个托盘（约 ${cost}）走了高价的单托盘 3PL — 增加一辆自有卡车可让它们留在自有车队。",

  "truck.header": "车辆 {label} · 第 {n} 趟",
  "truck.weight": "重量",
  "truck.area": "占地",
  "truck.lunch": "🍽 午休 {start} – {end}",
  "truck.departs": "装车 {load} · 出发 {depart}",
  "truck.etaPrefix": "预计：",
  "truck.twAfter": "{t} 后",
  "truck.twEarly": "（过早）",
  "truck.twBy": "{t} 前",
  "truck.twLate": "（晚 {min} 分钟）",
  "truck.devWeight": "超重 {kg} kg（占上限 {pct}%）",
  "truck.devOverweight": "超重 —— 超出车辆上限",
  "truck.devCapacity": "超出装载面积 —— {pct}%（一辆挂车装不下）",
  "truck.devHours": "超出每日工时 —— {h}h，超 12h 上限 {over}h",
  "truck.devLate": "{n} 单延误 —— 最多超时 {min} 分钟",
  "truck.devEarly": "{n} 个站点早于允许窗口到达",
  "truck.devTruckDay": "车辆 {label} 全天 {h}h —— 超过 12h 上限",
  "truck.threeplMeta": "{stops} 站 · {mi} mi · ${cost}",

  "map.depot": "发车仓",
  "map.truck": "车辆",
  "map.stop": "站点",
  "map.eta": "预计",
  "map.stopsUnit": "站",
  "map.unrouted": "等待安排车次",
  "trailer.rotated": "（旋转）",

  // ── 3PL bookings ──
  "bookings.title": "3PL 预订",
  "bookings.summary": "{n} 辆 3PL 卡车中已预订 {booked} 辆",
  "bookings.unassigned": "{n} 辆未指派承运商",
  "bookings.requested": "{n} 辆已询价",
  "bookings.totals": "议定价 {negotiated}，模型估价 {estimate}",
  "bookings.saveFirst": "请先重新校准以保存更改，再进行预订。",
  "bookings.col.truck": "卡车",
  "bookings.col.load": "货载",
  "bookings.col.carrier": "承运商",
  "bookings.col.state": "预订状态",
  "bookings.col.estimate": "估价",
  "bookings.col.negotiated": "议定价",
  "bookings.truck": "3PL 卡车 {truck}",
  "bookings.trips": "{n} 趟",
  "bookings.pallets": "{n} 托",
  "bookings.bookedElsewhere": "也在另一计划的已预订卡车上：{orders}",
  "bookings.choose": "选择承运商",
  "bookings.newCarrier": "新承运商",
  "bookings.carrierName": "承运商名称",
  "bookings.add": "添加",
  "bookings.similar": "列表中已有：{name}",
  "bookings.similarInactive": "列表中已有（已停用）：{name}",
  "bookings.useIt": "使用 {name}",
  "bookings.reactivate": "重新启用 {name}",
  "bookings.editCarrier": "编辑承运商",
  "bookings.carrierDetails": "{name} 详情",
  "bookings.field.name": "名称",
  "bookings.field.contact_name": "联系人",
  "bookings.field.phone": "电话",
  "bookings.field.email": "邮箱",
  "bookings.field.notes": "备注",
  "bookings.field.ushipment_supplier_id": "uShipment 供应商 ID",
  "bookings.save": "保存",
  "bookings.deactivate": "停用",
  "bookings.deactivateConfirm": "停用 {name}？重新启用前无法再预订；已有预订保留。",
  "bookings.state.unassigned": "无承运商",
  "bookings.state.requested": "已询价",
  "bookings.state.booked": "已预订",
  "bookings.changed": "预订后已有变更",
  "bookings.book": "预订",
  "bookings.request": "询价",
  "bookings.markBooked": "标记为已预订",
  "bookings.informed": "已通知承运商",
  "bookings.cancelBooking": "取消预订",
  "bookings.cancelRequest": "取消询价",
  "bookings.cancelReason": "取消原因？",
  "bookings.details": "费用与记录",
  "bookings.chargesFor": "{truck} 的费用",
  "bookings.kind": "类型",
  "bookings.kind.linehaul": "干线运费",
  "bookings.kind.accessorial": "附加费",
  "bookings.kind.adjustment": "调整",
  "bookings.amount": "金额（$）",
  "bookings.code": "代码（如 liftgate）",
  "bookings.note": "备注",
  "bookings.addCharge": "添加费用",
  "bookings.void": "作废",
  "bookings.voidReason": "作废此费用的原因？",
  "bookings.voided": "已作废：{reason}",
  "bookings.wasCancelled": "{carrier} · 已取消：{reason}",

  // ── Deliveries (read from Samsara) ──
  "deliveries.title": "送达记录",
  "deliveries.desc":
    "当天已发送线路上的每个订单，按 Samsara 的记录显示：何时离开仓库、何时到达卸货点和送达，" +
    "以及加盖 ANL 和拍摄地点水印的签收证明。「从 Samsara 刷新」立即读取当天状态；每晚整理已完成的线路。" +
    "可把订单的分享链接发给客户。",
  "deliveries.depot": "仓库",
  "deliveries.date": "配送日期",
  "deliveries.attentionOnly": "仅看需关注",
  "deliveries.summary": "当日概况",
  "deliveries.sum.orders": "订单",
  "deliveries.sum.in_progress": "进行中",
  "deliveries.sum.delivered": "已送达",
  "deliveries.sum.exceptions": "异常",
  "deliveries.sum.not_delivered": "未送达",
  "deliveries.sum.on_time": "准时",
  "deliveries.sum.pod_missing": "缺签收",
  "deliveries.sum.needs_attention": "需关注",
  "deliveries.refresh": "从 Samsara 刷新",
  "deliveries.refreshing": "正在读取 Samsara…",
  "deliveries.refreshFailed": "刷新失败：{msg}",
  "deliveries.simulated": "此环境未连接 Samsara（模拟发布），没有读取任何数据。",
  "deliveries.routesFailed": "无法从 Samsara 读取 {routes}（{reason}）；其余线路已更新。",
  "deliveries.lastRefresh": "最近从 Samsara 读取于 {time}",
  "deliveries.neverRefreshed": "尚未从 Samsara 读取",
  "deliveries.notRead": "这一天有已发送的线路，但尚未从 Samsara 读取。",
  "deliveries.lastRun": "最近整理于 {time}",
  "deliveries.timesIn": "时间为 {zone}",
  "deliveries.clock.title": "上下班打卡",
  "deliveries.clock.hint": "取自每位司机在 Samsara 中的出车前、收车后车辆检查。",
  "deliveries.clock.driver": "司机",
  "deliveries.clock.in": "上班",
  "deliveries.clock.out": "下班",
  "deliveries.clock.hours": "工时",
  "deliveries.clock.duration": "{h} 小时 {m} 分",
  "deliveries.neverRun": "尚未整理",
  "deliveries.loadFailed": "无法加载送达记录：{msg}",
  "deliveries.empty": "这一天没有送达记录。",
  "deliveries.col.order": "订单",
  "deliveries.col.customer": "客户",
  "deliveries.col.drop": "卸货点",
  "deliveries.col.truck": "车辆 · 承运商",
  "deliveries.col.window": "时间窗",
  "deliveries.col.pallets": "托盘",
  "deliveries.col.stage": "状态",
  "deliveries.col.attention": "需关注",
  "deliveries.reviewed": "已处理",
  "deliveries.review.title": "需关注",
  "deliveries.review.note": "处理说明",
  "deliveries.review.submit": "标记为已处理",
  "deliveries.review.done": "{who} 于 {time} 处理：{note}",
  "deliveries.col.pod": "签收",
  "deliveries.stage.scheduled": "待出发",
  "deliveries.stage.left_depot": "已离开仓库",
  "deliveries.stage.at_drop": "在卸货点",
  "deliveries.stage.delivered": "已送达",
  "deliveries.stage.delivery_exception": "异常",
  "deliveries.stage.not_delivered": "未送达",
  "deliveries.open": "进行中",
  "deliveries.openHint": "线路仍在进行，此状态可能还会变化。",
  "deliveries.eta": "预计 {time}",
  "deliveries.by": "{time} 前",
  "deliveries.after": "{time} 后",
  "deliveries.ago.min": "{n} 分钟前",
  "deliveries.ago.hour": "{n} 小时前",
  "deliveries.pod.driver": "司机",
  "deliveries.pod.dispatcher": "调度上传",
  "deliveries.proof.title": "调度上传的签收证明",
  "deliveries.proof.hint": "司机没有提交签收？上传签字的 BOL 或托盘照片（照片或 PDF，最大 10 MB）。",
  "deliveries.proof.file": "文件",
  "deliveries.proof.kind": "内容",
  "deliveries.proof.kind.signed_bol": "签字的 BOL",
  "deliveries.proof.kind.pallets": "卸货后的托盘",
  "deliveries.proof.kind.issue": "问题",
  "deliveries.proof.kind.other": "其他",
  "deliveries.proof.upload": "上传",
  "deliveries.proof.also": "同时用于本趟的其他订单",
  "deliveries.proof.uploading": "正在上传…",
  "deliveries.proof.by": "由 {who} 上传",
  "deliveries.proof.remove": "删除",
  "deliveries.proof.removeConfirm": "删除这个上传？",
  "deliveries.proof.openPdf": "打开 PDF",
  "deliveries.markHint": "请先上传签字的 BOL 或托盘照片。",
  "deliveries.podMissing": "缺失",
  "deliveries.flag.no_pod": "无签收",
  "deliveries.flag.pod_far_from_drop": "签收地点离卸货点远",
  "deliveries.flag.late": "迟到",
  "deliveries.flag.before_window": "早于时间窗",
  "deliveries.flag.skipped": "已跳过",
  "deliveries.flag.issue_reported": "报告了问题",
  "deliveries.flag.no_pod_location": "签收无定位",
  "deliveries.flag.route_not_completed": "线路未完成",
  "deliveries.flag.no_carrier_update": "承运商无更新",
  "deliveries.flag.samsara_disagrees": "Samsara 结果不一致",
  "deliveries.flag.pod_after_override": "改判后收到签收单",
  "deliveries.sum.follow_ups": "待跟进",
  "deliveries.col.followUp": "跟进",
  "deliveries.said.samsara": "Samsara",
  "deliveries.said.carrier": "承运商",
  "deliveries.said.dispatcher": "调度员 ✓",
  "deliveries.disp.awaiting_decision": "待决定",
  "deliveries.disp.redeliver": "重新配送",
  "deliveries.disp.returning_to_warehouse": "退回仓库途中",
  "deliveries.disp.at_warehouse": "已回仓库",
  "deliveries.disp.customer_pickup": "客户自提",
  "deliveries.disp.picked_up": "已自提",
  "deliveries.disp.cancelled": "已取消",
  "deliveries.filter.code": "异常原因",
  "deliveries.filter.source": "状态来源",
  "deliveries.followUps": "待跟进",
  "deliveries.followUps.empty": "没有待跟进的订单。",
  "deliveries.followUps.attempt": "配送次数",
  "deliveries.followUps.attemptN": "第 {n} 次",
  "deliveries.followUps.onPlan": "已排入 {date} 的计划",
  "deliveries.outcome.title": "记录结果",
  "deliveries.outcome.hintSamsara": "将覆盖 Samsara 的结果，请说明原因。",
  "deliveries.outcome.hintCarrier": "承运商报告的此订单情况。",
  "deliveries.outcome.code": "异常原因",
  "deliveries.outcome.pick": "请选择…",
  "deliveries.outcome.reason": "Samsara 有误的原因",
  "deliveries.outcome.disposition": "下一步",
  "deliveries.outcome.save": "保存",
  "deliveries.outcome.overrides": "覆盖 Samsara（{stage}）",
  "deliveries.outcome.same": "Samsara 已是此结果：请改为修改异常原因。",
  "deliveries.outcome.pickCode": "请选择未成功的原因。",
  "deliveries.outcome.needReason": "请说明 Samsara 有误的原因。",
  "deliveries.outcome.needNote": "「{code}」需要备注说明情况。",
  "deliveries.override.title": "调度员判定的结果",
  "deliveries.override.by": "由 {who} 于 {time} 设定：{reason}",
  "deliveries.override.agrees": "Samsara 结果一致。",
  "deliveries.override.samsaraSays": "Samsara 显示：{stage}。",
  "deliveries.override.reason": "改用 Samsara 结果的原因",
  "deliveries.override.revert": "改用 Samsara 结果",
  "deliveries.followUp.title": "异常与下一步",
  "deliveries.followUp.code": "异常原因：{code}",
  "deliveries.followUp.recode": "修改原因",
  "deliveries.followUp.now": "下一步：{disposition}",
  "deliveries.followUp.none": "此订单没有下一步。",
  "deliveries.followUp.note": "下一步备注",
  "deliveries.followUp.save": "设定下一步",
  "deliveries.followUp.noteCancel": "请说明取消原因。",
  "deliveries.review.keepHint": "保留人工结果：填写备注后复核；或在下方改用 Samsara 结果。",
  "deliveries.history.title": "历史记录",
  "deliveries.history.attempt": "第 {n} 次（共 {m} 次）",
  "deliveries.history.this": "当前",
  "deliveries.history.empty": "暂无记录。",
  "deliveries.history.kind.samsara_reading": "Samsara 读数",
  "deliveries.history.kind.carrier_report": "承运商报告",
  "deliveries.history.kind.trip_reset": "行程恢复待出发",
  "deliveries.history.kind.override": "人工改判",
  "deliveries.history.kind.revert": "改回 Samsara 结果",
  "deliveries.history.kind.recode": "修改原因",
  "deliveries.history.kind.disposition": "下一步",
  "deliveries.history.kind.proof_added": "上传凭证",
  "deliveries.history.kind.proof_removed": "删除凭证",
  "deliveries.history.kind.review": "已复核",
  "deliveries.carriedBy": "承运方",
  "deliveries.all": "全部",
  "deliveries.ownFleet": "自有车队",
  "deliveries.anyCarrier": "任一 3PL 承运商",
  "deliveries.noCarrier": "无承运商",
  "deliveries.source.samsara": "Samsara",
  "deliveries.source.manual": "手动",
  "deliveries.unassigned": "3PL 卡车 {truck}（{n} 个订单）尚未指派承运商。",
  "deliveries.openPlan": "打开计划",
  "deliveries.update.title": "整趟行程的承运商更新",
  "deliveries.update.hint": "该 3PL 承运商不使用 Samsara：请在此录入其报告的整趟行程情况，单个订单的结果在上方记录。",
  "deliveries.update.stage": "发生了什么",
  "deliveries.update.at": "时间",
  "deliveries.update.note": "备注",
  "deliveries.update.noteHint": "请在备注中说明情况。",
  "deliveries.update.scheduledHint": "将整趟行程恢复为待出发（用于误录），之后可取消该订舱。",
  "deliveries.update.trip": "记录整趟行程",
  "deliveries.export": "导出 CSV",
  "deliveries.details": "详情",
  "deliveries.ev.pickedUp": "{time} 离开仓库",
  "deliveries.ev.enRoute": "{time} 出发前往此卸货点",
  "deliveries.ev.visit": "{arrived} 到达 · {departed} 离开（现场 {min} 分钟）",
  "deliveries.ev.arrived": "{time} 到达，仍在卸货点",
  "deliveries.ev.notReached": "司机未到达此卸货点",
  "deliveries.ev.notYet": "尚未到达",
  "deliveries.ev.notTracked": "Samsara 没有记录到车辆到达此卸货点",
  "deliveries.ev.dispatcherProof": "调度已上传签收证明（见下方）",
  "deliveries.ev.pod": "{time} 在 {place} 提交签收",
  "deliveries.ev.noPlace": "未知位置",
  "deliveries.ev.podDistance": " · 距卸货点 {km} 公里",
  "deliveries.ev.noPod": "没有签收证明",
  "deliveries.ev.reportedAt": "{stage} {time}（承运商报告）",
  "deliveries.ev.reported": "{stage}（承运商报告）",
  "deliveries.ev.noReport": "承运商尚未报告结果",
  "deliveries.ev.problem": "报告的问题：{issue}",
  "deliveries.ev.note": "司机备注：{note}",
  "deliveries.photoFailed": "照片无法加载",
  "deliveries.shareLink": "分享链接",
  "deliveries.noLink": "暂无分享链接",
  "deliveries.copyLink": "复制分享链接",
  "deliveries.copied": "已复制",
  "deliveries.newLink": "新链接",
  "deliveries.newLinkConfirm": "替换此订单的分享链接？旧链接将无法再打开。",

  // ── Order page (public, opened by a share link) ──
  "order.title": "订单 {order}",
  "order.timesIn": "时间为 {zone}",
  "order.invalid": "此链接无效。请联系 ANL 获取配送信息。",
  "order.failed": "暂时无法加载此配送信息，请稍后再试。",
  "order.status.delivered": "已送达",
  "order.status.delivery_exception": "配送异常 —— 请联系 ANL",
  "order.status.not_delivered": "未送达",
  "order.next.rescheduled": "已尝试配送，将重新安排。",
  "order.next.redelivery_planned": "已安排重新配送。",
  "order.next.returned_to_warehouse": "货物已退回仓库。",
  "order.ev.pickedUp": "离开 ANL {depot} 仓库",
  "order.ev.arrived": "司机到达",
  "order.ev.left": "司机离开",
  "order.pallets": "{n} 个托盘",
  "order.pickedUp": "提货",
  "order.proof": "签收证明",
  "order.photo.pickup": "提货托盘",
  "order.photo.pallets": "卸货托盘",
  "order.photo.signed_bol": "已签收提单",
  "order.download": "下载",
  "order.openPdf": "打开 PDF",
  "order.footer": "由 ANL 分享。时间为送货地当地时间；照片已加盖拍摄地点水印。",

  "history.title": "历史记录",
  "history.desc": "每次优化都会保存。选择日期，打开某次运行查看方案与输入，或选两次进行对比。",
  "history.runDate": "运行日期",
  "history.noRuns": "— 无记录 —",
  "history.compare": "对比",
  "history.compareReady": "对比 2 次",
  "history.comparePartial": "对比 ({n}/2)",
  "history.noRunsDate": "该日期无运行记录",
  "history.open": "打开",
  "history.inputBtn": "↓ 输入",
  "history.downloadTip": "下载该次运行的原始输入（可重新上传的 CSV）",
  "history.backToRuns": "← 返回列表",
  "history.downloadInput": "下载输入",
  "history.runFailed": "本次运行失败：",
  "history.inputs": "输入",
  "history.source": "来源",
  "history.ordersSelected": "已选 {n} 个订单",
  "history.palletsIn": "{n} 个托盘",
  "history.fleet": "车队：{f26}×26ft · {f53}×53ft · {f3pl} 3PL",
  "history.departures": "出发 {list}",
  "history.compareRuns": "对比运行",
  "history.runA": "运行 A",
  "history.runB": "运行 B",
  "history.diffNote": "高亮行为两次运行的差异",

  "results.dispatchPlan": "派车计划 →",
  "plan.title": "派车计划",
  "plan.meta": "{depot} · 第 {v} 版",
  "plan.back": "← 返回",
  "plan.loading": "正在打开派车计划…",
  "plan.loadFailed": "无法打开派车计划：",
  "plan.retry": "重试",
  "plan.summary": "计划概览",
  "plan.date": "配送日期",
  "plan.status": "状态",
  "plan.routes": "线路",
  "plan.stops": "站点",
  "plan.shipments": "托盘",
  "plan.vehicles": "已分配车辆",
  "plan.drivers": "已分配司机",
  "plan.idle": "{n} 辆车空闲",
  "plan.idleDrivers": "{n} 名司机空闲",
  "plan.validation": "校验",
  "plan.validationStale": "已过期 —— 请保存",
  "plan.validating": "校准中…",
  "plan.notValidated": "尚未校验",
  "plan.validationFailedShort": "校验失败",
  "plan.validationCounts": "阻断 {b} · 警告 {w}",
  "plan.phase.draft": "草稿",
  "plan.phase.modified": "已修改",
  "plan.phase.validating": "校验中",
  "plan.phase.valid": "校验通过",
  "plan.phase.invalid": "存在阻断问题",
  "plan.phase.validation_error": "校验失败",
  "plan.phase.publishing": "发布中",
  "plan.phase.published": "已发布",
  "plan.phase.partially_published": "部分发布",
  "plan.phase.publish_failed": "发布失败",
  "plan.phase.closed": "已关闭",
  "plan.revalidate": "保存",
  "plan.lastSaved": "上次保存 {time}",
  "plan.exportExcel": "导出 Excel",
  "plan.exportSaveFirst": "请先保存：导出的是已保存的计划。",
  "plan.savedPlans": "已保存的计划",
  "plan.trackDeliveries": "在配送中跟踪",
  "plan.publish": "发布计划",
  "plan.publishing": "发布中…",
  "plan.retryFailed": "重试失败线路",
  "plan.publishDisabledTip": "请保存并解决所有阻断问题后再发布。",
  "plan.discard": "放弃修改",
  "plan.reset": "恢复为优化方案",
  "plan.map": "线路地图",
  "plan.mapStale": "地图已过期 —— 保存后显示你的修改。",
  "plan.threeplLane": "第三方物流线路 {n}",
  "plan.carrier3pl": "第三方物流",
  "plan.announceTruck3pl": "卡车 {truck}：所有车次交给第三方物流。",
  "plan.laneTimes": "{depart} 出发 · 最后一站 {last} · {mi} 英里 · 第三方费率 ${cost}",
  "plan.with3pl": "{fleet} + {n} 第三方",
  "plan.threeplCost": "第三方费用",
  "plan.direct3pl": "第三方直送（超大）",
  "plan.direct3plNote": "超出我们所有车辆的尺寸：每件单独由第三方物流配送。仅供查看，不可编辑。",
  "plan.customer": "客户",
  "plan.handedTo3pl": "交给第三方物流，不发送到 Samsara：{routes}。",
  "plan.loading.sequenced": "已为第三方线路排好停靠顺序",
  "plan.locked": "停靠顺序由你设定",
  "plan.unlock": "交给优化器排序",
  "plan.announceUnlocked": "{route}：优化器可以重新安排停靠顺序。",
  "plan.resequenced": "重新校准已调整停靠顺序",
  "plan.loading.verified": "装载已检查",
  "plan.loading.order_unverified": "托盘能装下；按你的停靠顺序装载未检查",
  "plan.loading.not_verified": "装载未能及时检查",
  "plan.loading.infeasible": "托盘无法全部装下",
  "plan.issue.loading_infeasible": "{route}：这些托盘无法全部装入该拖车。",
  "plan.issue.loading_not_verified": "{route}：装载未能及时检查。",
  "plan.issue.loading_order_unverified": "{route}：托盘能装下，但按你的停靠顺序装载未检查。",
  "plan.resetConfirm": "用优化器的原始方案替换当前计划？所有修改（无论是否已保存）都将被放弃。",
  "plan.announceReset": "计划已恢复为优化器的方案。",
  "plan.atBaselineTip": "当前已是优化器的方案。",
  "plan.discardConfirm": "放弃对此派车计划的未保存修改？",
  "plan.conflict": "此计划已在别处被修改（{message}）。请载入最新版本 —— 你未保存的修改将被放弃。",
  "plan.reloadLatest": "载入最新版本",
  "plan.refresh": "刷新计划",
  "plan.rejected": "服务器拒绝了这些修改：",
  "plan.requestFailed": "出错了：{message}",
  "plan.publishedBanner": "{by} 于 {at} 发布。线路已交给 Samsara 发布模块（集成上线前为模拟）。",
  "plan.partialBanner": "{total} 条线路中已有 {sent} 条送达司机。请重试其余线路：",
  "plan.partialBannerStarted": "{total} 条线路中已有 {sent} 条送达司机。以下线路重试也无法更改：",
  "plan.close": "关闭计划",
  "plan.refreshDelivery": "刷新配送状态",
  "plan.deliveryAsOf": "配送状态更新于 {time}",
  "plan.announceDelivery": "配送状态已刷新。",
  "plan.delivery.scheduled": "尚未到达",
  "plan.delivery.unassigned": "尚未到达",
  "plan.delivery.en_route": "前往途中",
  "plan.delivery.arrived": "{arrived} 到达",
  "plan.delivery.departed": "{arrived} 到达 · {departed} 离开",
  "plan.delivery.skipped": "已跳过",
  "plan.pod.not_started": "签收凭证未开始",
  "plan.pod.in_progress": "签收凭证填写中",
  "plan.pod.submitted": "签收凭证已提交",
  "plan.pod.archived": "签收凭证已归档",
  "plan.podPhotos": "签收照片",
  "plan.photoProcessing": "{label}（处理中）",
  "plan.closeConfirm":
    "按当前状态关闭此计划？已发送的线路仍由司机执行；其余线路不会再发送，计划也不能再编辑或重试。",
  "plan.closedBanner": "{by} 于 {at} 关闭。{total} 条线路中有 {sent} 条送达司机；其余未发送。",
  "plan.unknownError": "未知错误",
  "plan.stuckBanner": "发布被中断。可以安全重试 —— 已发送的线路不会重复发送。",
  "plan.inProgressBanner": "此计划正在发布…",
  "plan.validationFailed": "无法校验线路：{message}",
  "plan.trip": "第 {n} 趟",
  "plan.routeTimes": "{start} 装车 · {depart} 出发 · {back} 返回 · {h} 小时 · {mi} 英里",
  "plan.routeCost": "${cost}",
  "plan.noTiming": "尚未计算时间 —— 请保存",
  "plan.emptyRoute": "无站点 —— 此线路不会发布",
  "plan.staleShort": "已过期",
  "plan.vehicle": "车辆",
  "plan.driver": "司机",
  "plan.unassigned": "未分配",
  "plan.offToday": "今日休息",
  "plan.routeSent": "已发送给司机",
  "plan.routeFailed": "未发送：{error}",
  "plan.routeNotSent3pl": "未发送：由 3PL 承运",
  "plan.nothingToPublish": "没有可发布的内容：没有任何自有车队线路有订单。",
  "plan.weight": "重量",
  "plan.dueBy": "最晚 {time}",
  "plan.notBefore": "不早于 {time}",
  "plan.no53": "53′ 车无法进入",
  "plan.orderAria": "订单 {order}：{name}",
  "plan.moveEarlier": "上移 {order}",
  "plan.moveLater": "下移 {order}",
  "plan.announceEarlier": "{order} 已上移。",
  "plan.announceLater": "{order} 已下移。",
  "plan.announceMoved": "站点 {zip} 已移到 {route} 的第 {pos} 位。",
  "plan.announceMovedOrder": "订单 {order} 已移到 {route}。",
  "plan.announceUnload": "订单 {order} 在本站的卸货顺序已调整。",
  "plan.announceVehicle": "{route}：车辆改为 {v}。",
  "plan.announceDriver": "{route}：司机改为 {d}。",
  "plan.announceDeparture": "{truck}：出发时间改为 {at}。",
  "plan.announceValidated": "校验完成：{b} 个阻断，{w} 个警告。",
  "plan.validationTitle": "校验结果",
  "plan.staleNote": "有未保存的修改。保存后会重新规划各趟次。",
  "plan.sev.blocking": "阻断 —— 发布前必须解决",
  "plan.sev.warning": "警告 —— 确认后可发布",
  "plan.sev.info": "提示",
  "plan.noIssues": "未发现问题。",
  "plan.route": "线路",
  "plan.zipReason.no_street": "订单目的地详情中没有街道地址：司机只能导航到邮编区域。",
  "plan.zipReason.self_pickup": "标记为客户自提。",
  "plan.zipReason.zip_mismatch": "地址中的邮编与订单不符：请核对订单。",
  "plan.zipReason.not_geocoded": "尚未定位到地图：下次同步会查询此地址。",
  "plan.zipReason.no_match": "在该邮编内找不到此地址的街道级位置。",
  "plan.zipReason.unknown": "没有街道地址记录：司机只能导航到邮编区域。",
  "plan.zipReason.unplaced": "此地址未定位到地图：司机只能导航到邮编区域。",
  "plan.lateBy": "迟到 {m} 分钟",
  "plan.pallet": "托盘",
  "plan.dims": "尺寸（长×宽×高）",
  "plan.publishTitle": "将 {n} 条线路发布给司机？",
  "plan.retryTitle": "重试 {n} 条线路？",
  "plan.publishDesc": "每位司机会在 Samsara 应用中收到 {date} 的线路。发布后仍可修改计划，并发布修改。",
  "plan.departs": "出发",
  "plan.warningsToAck": "{n} 个警告需要确认",
  "plan.ackWarnings": "我已查看这些警告，仍要发布。",
  "plan.publishNote": "Samsara 尚未接入 —— 发布为模拟操作，并记录发布人和时间。",
  "plan.publishNoteLive": "每趟行程会发送到 Samsara 中对应的卡车，无论谁登录该卡车，都按卡车的 GPS 跟踪进度。",
  "plan.cancel": "取消",
  "plan.confirmPublish": "发布",
  "plan.confirmRetry": "重试",
  "plan.publishChanges": "发布修改",
  "plan.noChangesTip": "没有需要发送的修改：司机手上的计划已是最新。",
  "plan.changesTitle": "向 {n} 条线路发布修改？",
  "plan.changesDesc": "司机的 Samsara 应用会更新为与计划一致。司机已经开始的线路保持不变。",
  "plan.changesNew": "新线路",
  "plan.changesUpdated": "已更新的线路",
  "plan.changesRemoved": "从司机处移除",
  "plan.changesPending": "有尚未发送的修改：司机仍按上次发布的计划执行。发布修改后会更新他们的应用。",
  "plan.routeChanged": "已修改 —— 尚未发送给司机",
  "plan.routeToWithdraw": "将从司机的应用中移除",
  "plan.routeStarted": "司机已开始这条线路，无法在此修改。",
  "plan.routeWithdrawn": "已从 Samsara 清除",
  "plan.routeKeptStarted": "未改动：司机已开始这条线路",
  "plan.routeUpdateFailed": "司机仍持有上次发送的版本：{error}",
  "plan.withdraw": "从 Samsara 清除线路",
  "plan.withdrawTitle": "从 Samsara 清除 {n} 条线路？",
  "plan.withdrawDesc":
    "下列每条线路都会从司机的应用中删除。司机已经开始的线路保持不变。计划会回到草稿并保留你的修改，可修改后重新发布。",
  "plan.withdrawNote": "Samsara 尚未接入 —— 清除为模拟操作。",
  "plan.withdrawNoteLive": "线路会立即在 Samsara 中删除，司机将不再看到它们。",
  "plan.confirmWithdraw": "清除线路",
  "plan.withdrawing": "正在清除…",
  "plan.announceWithdrawn": "线路已从 Samsara 清除。",
  "plan.issue.vehicle_missing": "{route} 未分配车辆。",
  "plan.issue.vehicle_unknown": "{route}：车辆 {vehicle_id} 不在车队中。",
  "plan.issue.driver_missing": "{route} 未分配司机。",
  "plan.issue.driver_unknown": "{route}：司机 {driver_id} 不在名单中。",
  "plan.issue.driver_unavailable": "{route}：{driver} 今日不可用。",
  "plan.issue.driver_multiple_vehicles": "{route}：{driver} 被分配到多辆车。",
  "plan.issue.vehicle_multiple_trucks": "{route}：{vehicle} 被分配给多辆卡车。",
  "plan.issue.no_53ft_access": "{route}：邮编 {zip} 无法接收 53′ 拖车。",
  "plan.issue.pallet_does_not_fit": "{route}：托盘 {shipment_id} 放不进 {trailer_type} 拖车。",
  "plan.issue.plan_date_past": "配送日期 {plan_date} 已过。",
  "plan.issue.another_plan_published": "此仓库 {plan_date} 已有另一份计划发布 —— 司机可能收到重复线路。",
  "plan.issue.address_missing": "{n} 个卸货点没有街道级位置 —— 司机只能拿到邮编区域，到达记录为近似值。",
  "plan.issue.address_outside_area":
    "{route}：订单 {order_no} 的地址在 {address_zip}，不在本仓库的配送范围内 —— 仍按订单邮编 {zip} 安排。",
  "plan.issue.customer_pickup": "{route}：订单 {order_no} 标记为客户自提 —— 请确认是否应装车。",
  "plan.issue.route_empty": "{route} 没有站点，不会发布。",
  "plan.issue.excluded_shipments": "{n} 个托盘走第三方物流或无法安排 —— 不在本次派车中。",
  "plan.issue.overweight": "{route} 超出拖车重量上限 {weight_over_kg} kg。",
  "plan.issue.over_floor_area": "{route} 需要拖车地板面积的 {footprint_pct}%。",
  "plan.issue.late": "{route}：{order_no} 预计 {eta} 到达，比最晚时间 {deadline} 晚 {late_min} 分钟。",
  "plan.issue.early": "{route}：{order_no} 预计 {eta} 到达，早于 {after} 开始的时间窗。",
  "plan.issue.driver_hours": "{driver} 今日工作 {hours} 小时（超过 {limit} 小时）。",
  "plan.issue.departure_passed": "{route} 原定 {depart} 出发，已过：现在发送会立即开始，并可能以全部站点跳过结束。",
  // ── 调度计划：表格，以及调度员自己加的订单和线路 ──
  "plan.tableLabel": "按线路列出的订单",
  "plan.col.stop": "站",
  "plan.col.order": "订单",
  "plan.col.customer": "客户",
  "plan.col.zip": "邮编",
  "plan.col.pallets": "托盘",
  "plan.col.weight": "重量",
  "plan.col.window": "送货时间",
  "plan.col.eta": "预计到达",
  "plan.col.address": "地址",
  "plan.col.notes": "备注",
  "plan.col.status": "送货状态",
  "plan.col.actions": "操作",
  "plan.stopNumberFor": "邮编 {zip} 的站序",
  "plan.routeFor": "订单 {order} 所在线路",
  "plan.takeOff": "将 {order} 移出计划",
  "plan.showPallets": "显示 {order} 的托盘",
  "plan.hidePallets": "隐藏 {order} 的托盘",
  "plan.palletsOf": "{order} 的托盘",
  "plan.zipOnlyShort": "仅邮编 {zip}",
  "plan.badgeAdded": "已添加",
  "plan.badgeManual": "手动",
  "plan.addedRoute": "新增线路 {n}",
  "plan.truck": "卡车 {truck}",
  "plan.truckTrips": "{n} 个车次",
  "plan.addTruck": "添加卡车",
  "plan.addTrip": "添加车次",
  "plan.addTripTo": "为 {truck} 添加车次",
  "plan.removeTrip": "移除",
  "plan.removeTripOf": "移除 {route}",
  "plan.removeTruck": "移除卡车",
  "plan.removeTruckOf": "移除卡车 {truck}",
  "plan.lunchAt": "午休 {time}",
  "plan.zipOf": "邮编 {zip}",
  "plan.announceTripAdded": "已添加 {route}。",
  "plan.announceTruckAdded": "已添加卡车 {truck}，请选择车辆。",
  "plan.announceTripRemoved": "已移除 {route}。",
  "plan.announceTruckRemoved": "已移除卡车 {truck}。",
  "plan.loadPct": "载重 {w}% · 地板 {a}%",
  "plan.emptyRouteRow": "暂无订单：在订单上选择此线路、把订单拖到这里，或添加订单。",
  "plan.addOrders": "添加订单",
  "plan.addOrdersTo": "向 {route} 添加订单",
  "plan.unroutedTitle": "未排线路",
  "plan.unroutedNote": "已移出计划：不在本计划中配送，也不会发给司机。把订单放回某条线路即可配送。",
  "plan.putBackOn": "将 {order} 放回",
  "plan.putBackPlaceholder": "放回到…",
  "plan.announceTakenOff": "订单 {order} 已移出计划。",
  "plan.announcePutBack": "订单 {order} 已放回 {route}。",
  "plan.announceAdded": "已添加 {n} 个订单并重新校准。",
  "plan.resetAddedOrders": "{n} 个订单",
  "plan.resetAddedRoutes": "{n} 个车次",
  "plan.resetConfirmAdded": "这也会移除你添加的{what}。",
  "plan.and": "和",
  "plan.editAddress": "编辑 {order} 的地址",
  "plan.addressOf": "{order} 的地址",
  "plan.addressSave": "保存",
  "plan.useSystemAddress": "改回系统地址",
  "plan.addressEdited": "已修改",
  "plan.systemAddress": "系统地址：{address}",
  "plan.addressMovedZip": "按此地址所在邮编 {zip} 安排（订单邮编为 {own}）。",
  "plan.addressUnsaved": "保存后才会在地图上定位此地址。",
  "plan.announceAddress": "{order} 的地址已修改。",
  "plan.announceAddressBack": "{order} 的地址已改回系统地址。",
  "plan.editNotes": "编辑 {order} 的备注",
  "plan.notesOf": "{order} 的备注",
  "plan.notesSave": "保存备注",
  "plan.notesTooLong": "备注最多 {max} 个字符（当前 {n} 个）。",
  "plan.notesEdited": "已修改",
  "plan.systemNotes": "系统备注：{notes}",
  "plan.noNotes": "无备注",
  "plan.useSystemNotes": "改回系统备注",
  "plan.announceNotes": "{order} 的备注已修改。",
  "plan.announceNotesBack": "{order} 的备注已改回系统备注。",
  "plan.editWindow": "编辑 {order} 的时间窗",
  "plan.windowEdited": "已修改",
  "plan.windowAfter": "不早于",
  "plan.windowDeadline": "最晚送达",
  "plan.windowAfterOf": "{order} 不早于",
  "plan.windowDeadlineOf": "{order} 最晚送达",
  "plan.windowSave": "保存时间窗",
  "plan.windowBackwards": "“不早于”必须早于“最晚送达”。",
  "plan.windowUnsaved": "保存后才会按此时间窗重新计算线路。",
  "plan.systemWindow": "系统时间窗：{window}",
  "plan.systemNotBefore": "不早于 {time}",
  "plan.systemDueBy": "最晚 {time}",
  "plan.noWindow": "无时间窗",
  "plan.useSystemWindow": "改回系统时间窗",
  "plan.announceWindow": "{order} 的时间窗已修改。",
  "plan.announceWindowBack": "{order} 的时间窗已改回系统时间窗。",

  // ── 调度计划：订单选择 ──
  "add.title": "向计划添加订单",
  "add.desc": "从订单同步中选择订单，或录入同步中没有的订单。每个订单放到你选择的线路上并在该线路重新校准，不会重新优化。",
  "add.fromSync": "同步中的订单",
  "add.search": "搜索订单号或客户",
  "add.searchBtn": "搜索",
  "add.loading": "正在加载订单…",
  "add.loadFailed": "无法加载订单：{message}",
  "add.none": "没有找到订单。",
  "add.onPlan": "已在本计划：{where}",
  "add.allRegions": "所有仓库",
  "add.picked": "已选",
  "add.add": "添加 {order}",
  "add.paste": "粘贴订单号",
  "add.find": "查找",
  "add.notFound": "未找到：{list}",
  "add.alreadyOn": "已在本计划中：{list}",
  "add.manualLegend": "同步中没有的订单",
  "add.orderNo": "订单号",
  "add.customer": "客户",
  "add.zip": "邮编",
  "add.address": "地址",
  "add.notes": "给司机的备注",
  "add.addManual": "添加手动订单",
  "add.toAdd": "待添加",
  "add.nothingPicked": "还没有选择订单。",
  "add.remove": "移除 {order}",
  "add.route": "线路",
  "add.notBefore": "不早于",
  "add.dueBy": "最晚",
  "add.anyTime": "任意时间",
  "add.length_cm": "托盘 {n} 长度（cm）",
  "add.width_cm": "托盘 {n} 宽度（cm）",
  "add.height_cm": "托盘 {n} 高度（cm）",
  "add.weight_kg": "托盘 {n} 重量（kg）",
  "add.palletUnits": "每个托盘：长 × 宽 × 高（cm），重量（kg）",
  "add.addPallet": "添加托盘",
  "add.removePallet": "移除托盘 {n}",
  "add.loadingPallets": "正在加载托盘…",
  "add.confirm": "添加 {n} 个订单",
  "add.adding": "正在添加…",
  "add.unrouted": "暂不安排车次",
  // 手动排车（issue 46）
  "app.creatingPlan": "正在创建计划…",
  "picker.planByHand": "手动排车（{n} 个订单）",
  "picker.planByHandTip": "不运行优化器，用这些订单直接创建计划。",
  "picker.planByHandTitle": "手动排车",
  "picker.planByHandTrips": "计划以这里设置的卡车和车次开始，订单按你列出的顺序。可在计划页修改任何内容。",
  "picker.planByHandPool": "计划开始时这些订单都在等待安排车次。请在计划页添加卡车并把订单放上车次。",
  "picker.planByHandOnPlans": "其中 {n} 个订单已在其他计划中 —— 发布前计划页会提示。",
  "picker.planDate": "配送日期",
  "picker.cancel": "取消",
  "picker.createPlan": "创建计划",
  "picker.openPlanAt": "打开此订单所在的计划",
  "picker.statusCol": "状态",
  "picker.statusFilter": "状态",
  "picker.statusAll": "全部",
  "picker.statusFilter.available": "可排车",
  "picker.statusFilter.on_plan": "已在计划中",
  "picker.statusFilter.delivered": "已送达",
  "picker.statusFilter.exception": "异常",
  "status.available": "可排车",
  "status.planned": "已计划",
  "status.scheduled": "已安排",
  "status.left_depot": "已出仓",
  "status.at_drop": "在卸货点",
  "status.delivered": "已送达",
  "status.delivery_exception": "配送异常",
  "status.not_delivered": "未送达",
  "plan.originHand": "手动创建",
  "plan.originRun": "来自优化运行",
  "plan.startOver": "重新开始",
  "plan.startOverConfirm": "回到创建计划时的订单，全部不在车次上？所有修改（无论是否已保存）都将被放弃。",
  "plan.noTrucksYet": "还没有卡车。添加卡车后把订单放上它的车次。",
  "plan.noTrucks": "此计划没有卡车。",
  "plan.unroutedPoolNote": "等待安排车次：放上车次之前不会随本计划配送。勾选订单后放上车次。",
  "plan.pickAllUnrouted": "勾选所有等待中的订单",
  "plan.pickUnrouted": "勾选 {order}",
  "plan.putOnTrip": "把勾选的订单放上车次",
  "plan.putOnTripN": "把 {n} 个放上车次…",
  "plan.announcePutOnTrip": "{n} 个订单已放上 {route}。",
  "plan.openOtherPlan": "打开该计划",
  "plan.sequencePlanned": "停靠顺序在保存时规划",
  "picker.onPlansNote": "其中 {n} 个订单已在其他计划中。",
  "plan.issue.orders_unrouted": "{n} 个订单不在车次上，不会随本计划配送。",
  "plan.issue.order_on_another_plan": "{route}：订单 {order_no} 也在当天另一份计划的车次上 —— 可能重复配送。",
  "plan.issue.carrier_unassigned": "第三方卡车 {truck_id} 尚未询价或预订承运商。",
  "deliveries.searchLabel": "查找订单",
  "deliveries.searchHint": "订单号、托盘号或 uShipment 订单 ID",
  "deliveries.search": "搜索",
  "deliveries.clearSearch": "返回当天",
  "deliveries.searchFailed": "搜索失败：{msg}",
  "deliveries.searchResults": "搜索结果",
  "deliveries.notFound": "不在你的任何计划中。",
  "deliveries.notFoundAnywhere": "不在你的任何计划中，也不在订单同步中。",
  "deliveries.group": "进度",
  "deliveries.group.pending": "待配送",
  "deliveries.group.in_transit": "运输中",
  "deliveries.group.completed": "已完成",
  "deliveries.group.exception": "异常",
};

const messages: Record<Lang, Record<MsgKey, string>> = { en, zh };

export type Vars = Record<string, string | number>;

function interpolate(template: string, vars?: Vars): string {
  let out = template;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      out = out.split(`{${k}}`).join(String(v));
    }
  }
  // Regular-plural token: drop the "s" when a numeric `n` var equals 1.
  const n = vars?.n;
  const plural = n != null && Number(n) === 1 ? "" : "s";
  return out.split("{s}").join(plural);
}

interface LangCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const Ctx = createContext<LangCtx | null>(null);

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "zh") return saved;
  } catch {
    /* storage disabled */
  }
  return "zh"; // Chinese first-load default (the client's daily language).
}

// `initial` pins the starting language (the public order page is English for
// ANL's customers); without it, the saved choice or the Chinese default.
export function LangProvider({ children, initial }: { children: ReactNode; initial?: Lang }) {
  const [lang, setLangState] = useState<Lang>(initial ?? initialLang);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* storage disabled */
    }
  }, []);
  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLang must be used within <LangProvider>");
  return ctx;
}

// Translate hook. Returns a `t(key, vars?)` bound to the current language.
export function useT(): (key: MsgKey, vars?: Vars) => string {
  const { lang } = useLang();
  return useCallback(
    (key: MsgKey, vars?: Vars) => interpolate(messages[lang][key], vars),
    [lang],
  );
}
